import { useState, useRef, useEffect, useMemo } from "react";
import { orderQuotePositions } from "@/lib/quote-position-order";
import { ReorderableQuotePositions } from "./reorderable-quote-positions";
import { QuoteImageButton } from "./quote-image-button";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { InvoiceApi } from "@/lib/invoice-api";
import { invoiceFromQuote } from "@/lib/invoice-from-quote";
import { useRenderCounter } from "@/lib/loop-detector";
import { formatAmount, formatArea, formatDecimal, formatActivityLabel, capitalizeList } from "@/lib/formatters";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { GermanNumberInput } from "@/components/ui/german-number-input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Plus, Minus, FileText, Mail, Download, Euro, Calculator, Lock, LockOpen, Trash2, Building2, Image, Link2, Ruler, X, AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, ShieldCheck, Pencil, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { PDFGenerator, QuoteData, type PdfLayoutOptions, DEFAULT_PDF_LAYOUT, QUOTE_PDF_VARIANTS, quotePdfFilename, type QuotePdfVariant } from "@/lib/pdf-generator";
import { Slider } from "@/components/ui/slider";
import { Eye, Sliders } from "lucide-react";
import { EmailService, EmailTemplates } from "@/lib/email-service";
import { LocalStorageManager, type StoredQuoteData } from "@/lib/storage";
import { type MeasurementResult, getMeasurementAmount, getMeasurementUnit } from "./area-measurement";
import { STAMMDATEN, type LVPosition, type LVSummen, type MengenRefMap, type AnnotatedMenge } from "./leistungsverzeichnis-kalkulator";
import SaveOfferCalculationTemplate from "./save-offer-calculation-template";
import { assembleOfferTemplateSections } from "@/lib/offer-calculation-template";
import type { ProjectCalculations, ProjectSectionTitles } from "@/lib/project-position-calculation";
import { COMPANY, fetchNextQuoteNumber, generateQuoteNumber, getCompanyContactLines } from "@/lib/company-info";
import type { LibraryPosition } from "@shared/schema";
import {
  runPlausibilityChecks,
  summarizeFindings,
  type CheckPosition,
  type PlausibilityFinding,
  type PlausibilityFindingType,
} from "@/lib/quote-plausibility";

import { calculateOfferParts, normalizePartSettings, OPTIONAL_PART_NOTE, OFFER_PARTS, type OfferPart } from "@/lib/offer-parts";
import { OfferPartSelect } from "./offer-part-select";
import { OfferPartsPreview } from "./offer-parts-preview";
import { FormattedDescription } from "./formatted-description";
import { QuoteTextPlausibility } from "./quote-text-plausibility";
import type { MissingServicesImport } from "@/lib/quote-missing-services";

interface QuoteItem {
  teil?: OfferPart;
  id: string;
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  total: number;
  sourceLabel?: string;
  /** Vergleichsvariante – wird angezeigt, aber NICHT in die Summe gerechnet. */
  variant?: boolean;
  /** Optionaler Beschreibungstext (z.B. aus Foto-Analyse übernommen). */
  beschreibung?: string;
}

interface LocalQuoteData {
  projectName: string;
  customerName: string;
  customerAddress: string;
  items: QuoteItem[];
  notes: string;
  validUntil: string;
}

export interface PerPhotoBreakdownEntry {
  photoId?: string;
  photoLabel: string;
  fileName?: string;
  /** Erfasste Menge des zugehörigen Bildes (Einheit s. `unit`). */
  amount?: number;
  /** Einheit der erfassten Menge (z.B. m², Stück, lfm, …). */
  unit?: string;
  /** Optionale Bildquelle (URL/Data-URL) für das Vorschaubild im PDF-Header. */
  preview?: string;
  positions: Array<{
    posId?: string;
    sourceIndex?: number;
    teil?: OfferPart;
    name: string;
    /** Für dieses Foto individuell angepasster Leistungstext. */
    beschreibung?: string;
    qty: number;
    unit: string;
    ep: number;
    total: number;
    /** Vergleichsvariante – wird angezeigt, aber NICHT in die Zwischensumme gerechnet. */
    variant?: boolean;
  }>;
  subtotal: number;
}

export interface QuoteProjectDetails {
  activity?: string;
  objektbeschaffenheit?: string[];
  massnahmen?: string[];
  geschosszahl?: number | null;
  hoehe?: number | null;
  menge?: number | null;
  mengeneinheit?: string;
}

/**
 * Wiederherstellungs-Hint für die Plausibilitätsprüfung nach einem
 * „Zurück zur Plausibilitätsprüfung"-Klick im LV-Schritt. Enthält die zuletzt
 * angesehene Positions-Karte sowie alle bei der Sprung-Aktion aufgeklappten
 * Karten. `nonce` sorgt dafür, dass der Hint auch dann erneut wirkt, wenn der
 * Nutzer dieselbe Karte mehrfach hintereinander öffnet/schließt.
 */
export interface PlausibilityRestoreHint {
  posId: string;
  expandedPositionIds: string[];
  nonce: number;
}

interface QuoteGeneratorProps {
  onImportMissingServices?: (request: MissingServicesImport) => void | Promise<void>;
  onLvTeilChange?: (id: string, teil: OfferPart) => void;
  onPhotoTeilChange?: (photoId: string, sourceIndex: number | null, teil: OfferPart) => void;
  projectCalculations?: ProjectCalculations;
  projectSectionTitles?: ProjectSectionTitles;
  projectId?: number | string;
  projectName?: string;
  customerName?: string;
  projectAddress?: string;
  /** Freitext-Leistungs-/Projektbeschreibung aus Schritt 1 (Grunddaten).
   *  Wird im Stammdaten-Block des Angebots (On-Screen-Vorschau und PDF)
   *  unter den Schlüssel/Wert-Zeilen und vor der Leistungspositionen-Tabelle
   *  als eigener Absatz angezeigt. Bei leerem Text wird nichts gerendert. */
  projectDescription?: string | null;
  projectDetails?: QuoteProjectDetails;
  photos?: Array<{ id: string; file: File; preview?: string; type: string; serverPhotoId?: number; displayName?: string }>;
  documents?: Array<{ id: string; file: File; type: string }>;
  measurementResults?: Map<string, MeasurementResult>;
  lvPositionen?: LVPosition[];
  lvSummen?: LVSummen;
  lvMengenRefs?: MengenRefMap;
  onScrollToLvPosition?: (posId: string) => void;
  /**
   * Wird gerufen, wenn der Nutzer aus der Plausibilitätsprüfung in das LV
   * springt. `expandedPositionIds` enthält die im Plausi-Block gerade
   * aufgeklappten Positions-Karten — die Eltern-Komponente speichert diesen
   * Stand, damit ein späterer Rücksprung den Block wieder identisch öffnet.
   */
  onJumpFromPlausibility?: (posId: string, expandedPositionIds: string[]) => void;
  /**
   * Wird aus der Plausibilitätsprüfung gerufen, wenn der Nutzer den
   * Einzelpreis einer LV-Position direkt im Plausi-Block korrigiert.
   * Eltern-Komponente schreibt den Wert als manuellen EP-Override und
   * persistiert ihn — der LV-Kalkulator zieht den neuen Wert über
   * `initialEpOverrides` und markiert die Zeile als „MANUELL".
   */
  onUpdateLvPositionEp?: (lvPositionId: string, ep: number, unit?: string) => void;
  /**
   * Wenn gesetzt: Plausi-Block aufklappen, die angegebenen Karten aufklappen
   * und an die Karte mit `posId` scrollen. Wird vom Eltern nach Klick auf
   * „Zurück zur Plausibilitätsprüfung" (im LV-Schritt) übergeben.
   */
  plausibilityRestoreHint?: PlausibilityRestoreHint | null;
  /** Vom Eltern aufgerufen, sobald der Restore-Hint angewendet wurde. */
  onPlausibilityRestoreConsumed?: () => void;
  /** Wird gerufen, wenn der Nutzer beim PDF-Export oder E-Mail-Versand
   *  versucht ohne Leistungsbeschreibung weiterzugehen. Die Eltern-
   *  Komponente sollte zurück zu Schritt 1 navigieren und das
   *  Beschreibungs-Eingabefeld in den Sichtbereich scrollen + fokussieren. */
  onScrollToProjectDescription?: () => void;
  /**
   * Wird gerufen, wenn sich die nach LV-Position-ID gruppierten Plausibilitäts-
   * Befunde ändern. Die Eltern-Komponente reicht das an den LV-Kalkulator
   * weiter, damit dort pro LV-Zeile ein Warn-Symbol mit Tooltip eingeblendet
   * werden kann (Schnellzugriff in den Plausi-Block).
   */
  onPlausibilityFindingsByPosIdChange?: (
    map: Record<string, { count: number; titles: string[] }>,
  ) => void;
  availableMengen?: AnnotatedMenge[];
  perPhotoBreakdown?: PerPhotoBreakdownEntry[];
  /**
   * Vom Server geladener bzw. aus localStorage migrierter Initial-Stand des Angebots.
   * `null` bedeutet: Server hatte keinen Stand (frisches Angebot).
   * MUSS vor dem ersten Render der Komponente bekannt sein, daher rendert die
   * Eltern-Komponente den QuoteGenerator nur, wenn quoteLoadState !== 'not_loaded'.
   */
  initialQuoteData?: StoredQuoteData | null;
  /**
   * Wird bei jeder Änderung an persistierungsrelevanten Feldern aufgerufen.
   * Die Eltern-Komponente puffert (debounced 500ms) und schreibt zum Server.
   * Abgeleitete Items (LV-Positionen, Foto-Aggregate) werden NICHT mitgeschickt.
   */
  onQuoteChange?: (data: StoredQuoteData) => void;
}

export function QuoteGenerator({ onImportMissingServices, onLvTeilChange, onPhotoTeilChange, projectId, projectName, customerName, projectAddress, projectDescription, projectDetails, photos = [], documents = [], measurementResults, lvPositionen = [], lvSummen, lvMengenRefs = {}, projectCalculations = {}, projectSectionTitles = {}, onScrollToLvPosition, onJumpFromPlausibility, onUpdateLvPositionEp, plausibilityRestoreHint, onPlausibilityRestoreConsumed, onScrollToProjectDescription, onPlausibilityFindingsByPosIdChange, availableMengen = [], perPhotoBreakdown = [], initialQuoteData, onQuoteChange }: QuoteGeneratorProps) {
  const [, navigate] = useLocation();
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [offerParts, setOfferParts] = useState(() => normalizePartSettings(initialQuoteData?.offerParts));
  const [positionOrder, setPositionOrder] = useState<string[]>(() =>
    Array.isArray(initialQuoteData?.positionOrder) ? initialQuoteData.positionOrder.filter((id) => typeof id === "string") : []);
  const focusNewItemRef = useRef<string | null>(null);
  const [pdfExportVariant, setPdfExportVariant] = useState<QuotePdfVariant>(() =>
    QUOTE_PDF_VARIANTS.some((option) => option.value === initialQuoteData?.pdfExportVariant) ?
      initialQuoteData!.pdfExportVariant! : "gegliedert");
  const [pdfVariantDraft, setPdfVariantDraft] = useState<QuotePdfVariant>(pdfExportVariant);
  const [pdfVariantDialogOpen, setPdfVariantDialogOpen] = useState(false);
  const pdfVariantRef = useRef<QuotePdfVariant>("gegliedert");
  const pendingPdfActionRef = useRef<"download" | "preview" | "email">("download");
  useRenderCounter("QuoteGenerator");
  // Lazy-Initializer aus initialQuoteData → Server-Stand gewinnt.
  // initialQuoteData ist beim ersten Mount bereits final (Eltern rendert
  // erst nach Hydration), daher ist ein einfacher Initializer ausreichend.
  const [items, setItems] = useState<QuoteItem[]>(() => {
    // Manuelle Zusatzpositionen kommen ausschließlich aus dem persistierten
    // Stand. Frühere Versionen haben hier eine hartcodierte Demo-Zeile
    // ("Oberflächenreinigung und Vorbereitung – 250 € Pauschal") als Fallback
    // injiziert, sobald `manualItems` undefined oder leer war. Das hat dazu
    // geführt, dass die Zeile nach jedem Reload wiederkam, obwohl der Nutzer
    // sie aus dem LV/Angebot entfernen wollte. Daher: keine Demo-Daten mehr.
    if (!initialQuoteData?.manualItems) return [];
    return initialQuoteData.manualItems.map(it => ({
      id: it.id,
      teil: it.teil,
      description: it.description,
      quantity: it.quantity,
      unit: it.unit,
      unitPrice: it.unitPrice,
      total: it.total,
    }));
  });

  const [customerData, setCustomerData] = useState(() => ({
    name: initialQuoteData?.customerData?.name ?? (customerName || ''),
    address: initialQuoteData?.customerData?.address ?? (projectAddress || ''),
    email: initialQuoteData?.customerData?.email ?? '',
  }));
  const [addressManuallyEdited, setAddressManuallyEdited] = useState<boolean>(
    () => initialQuoteData?.addressManuallyEdited ?? false,
  );
  const [openPopoverItemId, setOpenPopoverItemId] = useState<string | null>(null);
  const [openPreviewPopoverItemId, setOpenPreviewPopoverItemId] = useState<string | null>(null);
  const [openLockPopoverItemId, setOpenLockPopoverItemId] = useState<string | null>(null);

  // IDs von LV- bzw. Foto-Aggregat-Positionen, die der Nutzer aus dem Angebot
  // entfernt oder entsperrt hat. Diese IDs werden beim LV-Sync und beim
  // photoAggregateItems-Merge gefiltert, damit sie nicht wiederkehren.
  const [suppressedLvIds, setSuppressedLvIds] = useState<Set<string>>(
    () => new Set(initialQuoteData?.suppressedLvIds ?? []),
  );
  const [suppressedPhotoAggIds, setSuppressedPhotoAggIds] = useState<Set<string>>(
    () => new Set(initialQuoteData?.suppressedPhotoAggIds ?? []),
  );
  const [lightboxImage, setLightboxImage] = useState<{ src: string; alt: string } | null>(null);

  useEffect(() => {
    if (addressManuallyEdited) return;
    const nextAddress = projectAddress || '';
    // Equality-Guard: nur schreiben, wenn sich die Adresse semantisch ändert.
    // Verhindert neue customerData-Object-Identity (und damit Folge-Re-Renders),
    // wenn nach einem Save `project` eine neue Referenz hat, `project.address`
    // aber inhaltlich unverändert ist.
    setCustomerData(prev => {
      if (prev.address === nextAddress) return prev;
      return { ...prev, address: nextAddress };
    });
  }, [projectAddress, addressManuallyEdited]);

  const [notes, setNotes] = useState<string>(() => initialQuoteData?.notes ?? '');
  const [validUntil, setValidUntil] = useState<string>(() => {
    if (initialQuoteData?.validUntil) return initialQuoteData.validUntil;
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date.toISOString().split('T')[0];
  });
  const [quoteDate, setQuoteDate] = useState<string>(
    () => initialQuoteData?.quoteDate ?? new Date().toISOString().split('T')[0],
  );
  const [quoteNumberEditing, setQuoteNumberEditing] = useState(false);
  const [quoteNumberDraft, setQuoteNumberDraft] = useState('');
  
  const [showMeasurementSources, setShowMeasurementSources] = useState<boolean>(() => {
    const stored = localStorage.getItem('showMeasurementSources');
    return stored === null ? true : stored === 'true';
  });

  const toggleShowMeasurementSources = (val: boolean) => {
    setShowMeasurementSources(val);
    localStorage.setItem('showMeasurementSources', String(val));
  };

  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  // Layout-Einstellungen für die PDF — werden ausschließlich in der UI gehalten
  // und sowohl an die Live-Vorschau als auch an Download/E-Mail durchgereicht.
  const [pdfLayout, setPdfLayout] = useState<Required<PdfLayoutOptions>>(() => ({
    ...DEFAULT_PDF_LAYOUT,
  }));
  const updateLayout = (key: keyof PdfLayoutOptions, value: number) => {
    setPdfLayout(prev => ({ ...prev, [key]: value }));
  };
  const resetLayout = () => setPdfLayout({ ...DEFAULT_PDF_LAYOUT });
  // Stabile Angebotsnummer pro Projekt — wird ebenfalls persistiert.
  // Angebotsnummer: bei bereits gespeicherten Angeboten wird die persistierte
  // Nummer wieder eingelesen. Bei neu angelegten Angeboten holen wir asynchron
  // eine fortlaufende Nummer vom Server (HS-{Jahr}-{4-stellig}). Bis die Antwort
  // da ist, zeigt die UI einen sicheren Zufalls-Fallback (gleiches Format), der
  // beim Speichern überschrieben wird, sobald die Server-Nummer eintrifft.
  const [quoteNumber, setQuoteNumber] = useState<string>(
    () => initialQuoteData?.quoteNumber ?? generateQuoteNumber(),
  );
  useEffect(() => {
    if (initialQuoteData?.quoteNumber) return;
    let cancelled = false;
    void fetchNextQuoteNumber().then((num) => {
      if (!cancelled) setQuoteNumber(num);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [selectedPhotoIds, setSelectedPhotoIds] = useState<Set<string>>(() => {
    // WICHTIG: gespeicherte selectedPhotoIds können aus älteren Sessions
    // veraltete transient FileUpload-IDs enthalten (Format: `${Date.now()}-…`),
    // die nach einem Reload nirgendwo mehr in `photos` vorkommen.
    // Wir filtern beim Initialisieren strikt auf die aktuell vorhandenen Foto-IDs,
    // damit downstream-Render (Foto-Auswahl-UI, PDF-Build) konsistent bleibt
    // und keine Stale-IDs persistiert werden.
    const validIds = new Set(photos.map((p) => p.id));
    if (initialQuoteData?.selectedPhotoIds) {
      const filtered = initialQuoteData.selectedPhotoIds.filter((id) => validIds.has(id));
      return new Set(filtered);
    }
    const measurementIds = new Set<string>();
    return new Set(photos.filter((p) => !measurementIds.has(p.id)).map((p) => p.id));
  });

  // ────────────────────────────────────────────────────────────────────────
  // Persistenz-Bridge: bei Änderungen an editierbaren Feldern an Eltern senden.
  // Eltern (project-edit) puffert (500ms) und schreibt zum Server.
  //
  // Wichtig gegen Save-Loop (React #185):
  // Mehrere Upstream-Effekte (Z. 124 setCustomerData, Z. 213 setSelectedPhotoIds,
  // Z. 254 setItems sowie Re-Renders durch Parent über LV-Kalkulator-Emits)
  // erzeugen bei jedem Render neue Object-/Array-Referenzen, OHNE dass sich
  // semantisch etwas geändert hat. Ein reiner Dep-Trigger würde dann bei jedem
  // Render scheduleQuoteSave aufrufen → LocalStorageManager.saveProject loggt
  // → Eltern speichert → Re-Render → … bis React abbricht.
  //
  // Lösung: lastEmittedHashRef hält einen Hash des zuletzt EMITTIERTEN Payloads
  // (OHNE updatedAt, das immer differieren würde). Nur ein inhaltlich neuer
  // Payload triggert onQuoteChange. Damit ist Re-Entry strukturell ausgeschlossen,
  // egal welcher Upstream-State seine Ref neu erzeugt.
  //
  // Die erste Emission nach Mount wird zusätzlich unterdrückt, sonst würde der
  // Server unmittelbar nach Hydration mit dem exakt-selben Stand überschrieben.
  // ────────────────────────────────────────────────────────────────────────
  const skipNextChangeRef = useRef(true);
  const lastEmittedHashRef = useRef<string | null>(null);
  useEffect(() => {
    if (!onQuoteChange) return;
    const manualItems = items
      .filter(it => !it.id.startsWith('lv-') && !it.id.startsWith('pp-agg-'))
      .map(it => ({
        id: it.id,
        teil: it.teil,
        description: it.description,
        quantity: it.quantity,
        unit: it.unit,
        unitPrice: it.unitPrice,
        total: it.total,
      }));
    // Stabiler Inhalts-Hash: alle persistierten Felder, sortierte Photo-IDs,
    // KEIN updatedAt. Damit ist der Hash deterministisch für identischen Inhalt.
    const sortedPhotoIds = Array.from(selectedPhotoIds).sort();
    const sortedSuppressedLvIds = Array.from(suppressedLvIds).sort();
    const sortedSuppressedPhotoAggIds = Array.from(suppressedPhotoAggIds).sort();
    const contentHash = JSON.stringify({
      positionOrder,
      pdfExportVariant,
      offerParts,
      customerData,
      addressManuallyEdited,
      notes,
      validUntil,
      quoteDate,
      selectedPhotoIds: sortedPhotoIds,
      manualItems,
      quoteNumber,
      suppressedLvIds: sortedSuppressedLvIds,
      suppressedPhotoAggIds: sortedSuppressedPhotoAggIds,
    });
    if (skipNextChangeRef.current) {
      skipNextChangeRef.current = false;
      lastEmittedHashRef.current = contentHash;
      return;
    }
    if (lastEmittedHashRef.current === contentHash) {
      // Nur Referenzen haben sich geändert, Inhalt ist identisch → kein Save.
      return;
    }
    lastEmittedHashRef.current = contentHash;
    onQuoteChange({
      positionOrder,
      pdfExportVariant,
      offerParts,
      schemaVersion: 1,
      customerData,
      addressManuallyEdited,
      notes,
      validUntil,
      quoteDate,
      selectedPhotoIds: sortedPhotoIds,
      manualItems,
      quoteNumber,
      suppressedLvIds: sortedSuppressedLvIds,
      suppressedPhotoAggIds: sortedSuppressedPhotoAggIds,
      updatedAt: new Date().toISOString(),
    });
  }, [
    positionOrder,
    pdfExportVariant,
    offerParts,
    customerData,
    addressManuallyEdited,
    notes,
    validUntil,
    quoteDate,
    selectedPhotoIds,
    items,
    quoteNumber,
    suppressedLvIds,
    suppressedPhotoAggIds,
    onQuoteChange,
  ]);

  // nonMeasurementPhotos memoisiert, damit nachgelagerte Render/Effekte stabile
  // Referenzen sehen, solange sich `photos` und `measurementResults` inhaltlich
  // nicht ändern. Ohne useMemo bekam jeder Render eine frische Array-Referenz —
  // in Kombination mit dem Sync-Effekt unten und Radix-Checkbox-State führte das
  // beim Tab-Wechsel auf das Angebot nach einem Foto-Delete zu React #185.
  const nonMeasurementPhotos = useMemo(() => {
    const measurementIds = new Set(measurementResults ? Array.from(measurementResults.keys()) : []);
    return photos.filter(p => !measurementIds.has(p.id));
  }, [photos, measurementResults]);

  // Stabiler Schlüssel über die ID-Menge (sortiert), damit der Sync auch greift,
  // wenn sich Foto-IDs ändern OHNE dass sich die Länge ändert (z.B. ein Foto wurde
  // gelöscht und parallel ein neues hochgeladen). `photos.length` allein hätte
  // diese Transition verpasst.
  //
  // Zusätzlich Aufmaß-Schlüssel mit ins Hash-Material: wenn ein Foto in/aus
  // measurementResults rein-/rausgeht, ändert sich der Anteil "nonMeasurement"
  // — der Sync-Effekt muss dann ebenfalls neu laufen.
  const photoIdsKey = photos.map((p) => p.id).sort().join("|");
  const measurementIdsKey = measurementResults
    ? Array.from(measurementResults.keys()).sort().join("|")
    : "";
  useEffect(() => {
    setSelectedPhotoIds(prev => {
      let changed = false;
      // 1) Stale-IDs entfernen, die nicht mehr in `photos` vorkommen.
      //    Schützt gegen veraltete transient-IDs aus älteren Server-/LocalStorage-Ständen.
      const validIds = new Set(photos.map((p) => p.id));
      const measurementIds = new Set(measurementResults ? Array.from(measurementResults.keys()) : []);
      const next = new Set<string>();
      prev.forEach((id) => {
        if (validIds.has(id)) next.add(id);
        else changed = true;
      });
      // 2) Neue, noch nicht selektierte Foto-IDs hinzufügen (Default: alle ausgewählt).
      //    WICHTIG: aus derselben Quelle wie `validIds` ableiten (photos ohne
      //    Measurement-Treffer), nicht aus dem gefilterten Render-Derivat
      //    `nonMeasurementPhotos`. Dadurch ist der Effekt deterministisch
      //    abhängig von photoIdsKey + measurementIdsKey.
      photos.forEach((p) => {
        if (measurementIds.has(p.id)) return;
        if (!next.has(p.id)) { next.add(p.id); changed = true; }
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photoIdsKey, measurementIdsKey]);
  
  const { toast } = useToast();
  const pdfGenerator = new PDFGenerator();
  const emailService = new EmailService();
  const downloadRef = useRef<HTMLAnchorElement>(null);
  const [pdfDialog, setPdfDialog] = useState<{ url: string; filename: string } | null>(null);
  const pdfDialogUrlRef = useRef<string | null>(null);
  const pdfDialogMountedRef = useRef(true);
  const pdfGenerationInFlightRef = useRef(false);
  const pdfActionRef = useRef<"download" | "preview">("download");
  const closePdfDialog = () => {
    setPdfDialog(null);
    if (pdfDialogUrlRef.current) URL.revokeObjectURL(pdfDialogUrlRef.current);
    pdfDialogUrlRef.current = null;
  };
  useEffect(() => {
    pdfDialogMountedRef.current = true;
    return () => {
      pdfDialogMountedRef.current = false;
      if (pdfDialogUrlRef.current) URL.revokeObjectURL(pdfDialogUrlRef.current);
      pdfDialogUrlRef.current = null;
    };
  }, []);

  const downloadDialogPdf = (document: { url: string; filename: string }) => {
    // A silent browser/sandbox rejection cannot be detected from anchor.click().
    // Keep the in-app preview available in every case, without popup navigation.
    try {
      if (!downloadRef.current) throw new Error("Download-Link nicht verfügbar");
      downloadRef.current.href = document.url;
      downloadRef.current.download = document.filename;
      downloadRef.current.click();
      toast({ title: "Download angefordert", description: "Falls der Browser den Download blockiert, bleibt das PDF in der Vorschau geöffnet." });
    } catch {
      toast({ title: "PDF in der Vorschau geöffnet", description: "Der Browser konnte den Download nicht starten. Sie können es im Dialog erneut versuchen." });
    }
  };

  const measurementPdfData = (measurementResults && measurementResults.size > 0)
    ? Array.from(measurementResults.entries()).map(([photoId, result]) => {
        const idx = photos.findIndex((p) => p.id === photoId);
        const photo = photos[idx];
        // Originalfoto verwenden (nicht die Annotation-Overlay-Version),
        // damit die PDF-Darstellung der Aufmaß-Karten der App-Ansicht
        // entspricht.
        const originalPreview = photo?.preview
          ?? (photo?.serverPhotoId ? `/api/photos/${photo.serverPhotoId}` : undefined);
        return {
          photoName: `Foto ${idx + 1}`,
          fileName: photo?.displayName?.trim() || photo?.file?.name,
          preview: originalPreview,
          amount: getMeasurementAmount(result),
          unit: getMeasurementUnit(result),
        };
      })
    : undefined;

  const lvKey = JSON.stringify(
    lvPositionen.map((p) => ({ id: p.id, teil: p.teil, name: p.name, qty: p.qty, unit: p.unit, ep: p.ep, variant: !!p.variant, beschreibung: p.beschreibung ?? null }))
  );
  const lvRefsKey = JSON.stringify(lvMengenRefs);
  // Stabiler String-Key über die suppressedLvIds-Menge, damit der LV-Sync-Effekt
  // neu läuft, wenn der Nutzer eine LV-Position aus dem Angebot entfernt/entsperrt.
  const suppressedLvIdsKey = Array.from(suppressedLvIds).sort().join(',');

  useEffect(() => {
    setItems(prev => {
      const manualItems = prev.filter(item => !item.id.startsWith('lv-'));
      const lvItems: QuoteItem[] =
        lvPositionen.length === 0
          ? []
          : lvPositionen
              // Positionen mit Menge 0 erscheinen nicht im Angebot/PDF.
              // Varianten sind reine Vergleichszeilen und gehen unverändert
              // mit raus, auch wenn ihre Menge 0 ist.
              // Vom Nutzer unterdrückte Positionen werden ebenfalls nicht übernommen.
              .filter((pos) => (!!pos.variant || pos.qty > 0) && !suppressedLvIds.has(pos.id))
              .map((pos) => {
                const angepassterLeistungstext =
                  typeof pos.beschreibung === "string" ? pos.beschreibung.trim() : "";
                return {
                  id: `lv-${pos.id}`,
                  description: angepassterLeistungstext || pos.name,
                  quantity: pos.qty,
                  unit: pos.unit,
                  unitPrice: pos.ep,
                  total: pos.qty * pos.ep,
                  sourceLabel: lvMengenRefs[pos.id]?.sourceLabel,
                  variant: !!pos.variant,
                  teil: pos.teil,
                };
              });
      const next = lvItems.length === 0 ? manualItems : [...lvItems, ...manualItems];
      // Inhalts-Guard gegen React #185: nur eine neue Items-Referenz schreiben,
      // wenn sich das Resultat semantisch von `prev` unterscheidet. Sonst würde
      // jeder Render eine neue Array-Identity erzeugen, der L230-Hash-Effekt
      // refeuern und (kombiniert mit dem Radix-Checkbox-Tree der Foto-Auswahl)
      // den Update-Loop hochziehen.
      if (next.length === prev.length) {
        let same = true;
        for (let i = 0; i < next.length; i++) {
          const a = next[i];
          const b = prev[i];
          if (
            a.id !== b.id ||
            a.teil !== b.teil ||
            a.description !== b.description ||
            a.quantity !== b.quantity ||
            a.unit !== b.unit ||
            a.unitPrice !== b.unitPrice ||
            a.total !== b.total ||
            (a.sourceLabel ?? '') !== (b.sourceLabel ?? '') ||
            !!a.variant !== !!b.variant ||
            (a.beschreibung ?? '') !== (b.beschreibung ?? '')
          ) {
            same = false;
            break;
          }
        }
        if (same) return prev;
      }
      return next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lvKey, lvRefsKey, suppressedLvIdsKey]);

  const addItem = () => {
    const newItem: QuoteItem = {
      id: crypto.randomUUID(),
      description: '',
      quantity: 1,
      unit: 'm²',
      unitPrice: 0,
      total: 0
    };
    focusNewItemRef.current = newItem.id;
    setItems((previous) => [...previous, newItem]);
    setPositionOrder([...mergedItems.map((item) => item.id), newItem.id]);
  };
  useEffect(() => {
    const id = focusNewItemRef.current;
    if (!id) return;
    const row = Array.from(document.querySelectorAll<HTMLElement>("[data-quote-position-id]"))
      .find((element) => element.dataset.quotePositionId === id);
    if (!row) return;
    row.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
    row.querySelector<HTMLInputElement>("input")?.focus({ preventScroll: true });
    focusNewItemRef.current = null;
  }, [items]);

  const removeItem = (id: string) => {
    if (id.startsWith('lv-')) {
      const posId = id.slice(3);
      setSuppressedLvIds(prev => new Set([...prev, posId]));
      setOpenLockPopoverItemId(null);
      toast({ title: "Position entfernt", description: "Die LV-Position wurde aus dem Angebot entfernt." });
      return;
    }
    if (id.startsWith('pp-agg-')) {
      const posId = id.slice(7);
      setSuppressedPhotoAggIds(prev => new Set([...prev, posId]));
      setOpenLockPopoverItemId(null);
      toast({ title: "Position entfernt", description: "Die Foto-Position wurde aus dem Angebot entfernt." });
      return;
    }
    setItems(prev => prev.filter(item => item.id !== id));
    setOpenLockPopoverItemId(null);
  };

  const unlockItem = (id: string, sourceItem: QuoteItem) => {
    const newId = `free-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const freeItem: QuoteItem = { ...sourceItem, id: newId };
    if (id.startsWith('lv-')) {
      const posId = id.slice(3);
      setSuppressedLvIds(prev => new Set([...prev, posId]));
      setItems(prev => {
        // freie Kopie an der Stelle einfügen, wo die LV-Position war
        const idx = prev.findIndex(it => it.id === id);
        if (idx >= 0) {
          const next = [...prev];
          next.splice(idx, 1, freeItem);
          return next;
        }
        return [...prev, freeItem];
      });
    } else if (id.startsWith('pp-agg-')) {
      const posId = id.slice(7);
      setSuppressedPhotoAggIds(prev => new Set([...prev, posId]));
      setItems(prev => [...prev, freeItem]);
    } else {
      return;
    }
    setOpenLockPopoverItemId(null);
    toast({
      title: "Position freigegeben",
      description: "Die Position wurde vom LV gelöst und kann jetzt frei bearbeitet werden.",
    });
  };

  const updateItem = (id: string, field: keyof QuoteItem, value: string | number) => {
    if (id.startsWith('lv-')) return;
    if (id.startsWith('pp-agg-')) return;
    setItems(items.map(item => {
      if (item.id === id) {
        const updatedItem = { ...item, [field]: value };
        
        if (field === 'quantity' || field === 'unitPrice') {
          updatedItem.total = updatedItem.quantity * updatedItem.unitPrice;
        }
        
        return updatedItem;
      }
      return item;
    }));
  };

  // Foto-Positionen, deren posId nicht bereits über eine aktive LV-Hauptposition
  // (qty > 0) abgedeckt ist, werden je posId aggregiert und als virtuelle
  // Positionen in den Angebotsbetrag einbezogen. So fließen pro Foto erfasste
  // Mengen automatisch in Nettobetrag/MwSt./Gesamt ein, ohne dass der Nutzer
  // sie zuerst manuell ins LV übernehmen muss. Doppelzählungen werden über die
  // posId-Prüfung gegen die aktive LV-Liste ausgeschlossen.
  const activeLvPosIds = new Set(
    lvPositionen.filter((p) => p.qty > 0).map((p) => p.id)
  );
  type AggregatedPhotoItem = {
    posId: string;
    name: string;
    beschreibung?: string;
    unit: string;
    ep: number;
    qty: number;
    total: number;
  };
  const aggregatedPhotoItems = new Map<string, AggregatedPhotoItem>();
  perPhotoBreakdown.forEach((entry) => {
    entry.positions.forEach((pos) => {
      if (!pos.posId) return;
      if (pos.variant) return; // Varianten nicht in die LV-Aggregation übernehmen
      if (activeLvPosIds.has(pos.posId)) return;
      const existing = aggregatedPhotoItems.get(pos.posId);
      if (existing) {
        existing.qty += pos.qty;
        existing.total += pos.total;
        if (!existing.beschreibung && pos.beschreibung?.trim()) {
          existing.beschreibung = pos.beschreibung.trim();
        }
      } else {
        aggregatedPhotoItems.set(pos.posId, {
          posId: pos.posId,
          name: pos.name,
          beschreibung: pos.beschreibung,
          unit: pos.unit,
          ep: pos.ep,
          qty: pos.qty,
          total: pos.total,
        });
      }
    });
  });
  const photoAggregateItems: QuoteItem[] = Array.from(aggregatedPhotoItems.values()).map((agg) => ({
    id: `pp-agg-${agg.posId}`,
    description: agg.beschreibung?.trim() || agg.name,
    quantity: agg.qty,
    unit: agg.unit,
    unitPrice: agg.ep,
    total: agg.total,
    sourceLabel: 'Aus Foto-Mengen',
  }));

  // Wenn eine Foto-Aufschlüsselung vorhanden ist, werden die schadensbezogenen
  // Mengen ausschließlich dort dargestellt. In der Hauptliste der
  // Leistungspositionen erscheinen dann nur noch die manuell gepflegten /
  // LV-aktivierten Positionen. Andernfalls bleibt das bisherige Fallback-
  // Verhalten erhalten (aggregierte Foto-Positionen werden in die Hauptliste
  // gemerged), damit Angebote ohne Foto-Aufschlüsselung weiter funktionieren.
  const hasPerPhotoBreakdown = perPhotoBreakdown.length > 0;
  const unorderedItems: QuoteItem[] = hasPerPhotoBreakdown
    ? [...items]
    : [...items, ...photoAggregateItems.filter(it => !suppressedPhotoAggIds.has(it.id.slice(7)))];
  const mergedItems = orderQuotePositions(unorderedItems, positionOrder);

  // Berechnungen – Varianten sind reine Vergleichszeilen und gehen NICHT in die Summe.
  const itemsVariantSubtotal = mergedItems.reduce((sum, item) => item.variant ? sum + item.total : sum, 0);
  const perPhotoVariantSubtotal = hasPerPhotoBreakdown
    ? perPhotoBreakdown.reduce(
        (sum, entry) => sum + entry.positions.reduce(
          (entrySum, pos) => pos.variant ? entrySum + pos.total : entrySum,
          0,
        ),
        0,
      )
    : 0;
  const variantSubtotal = itemsVariantSubtotal + perPhotoVariantSubtotal;
  const partTotals = calculateOfferParts([
    ...mergedItems,
    ...(hasPerPhotoBreakdown ? perPhotoBreakdown.flatMap((entry) => entry.positions) : []),
  ], offerParts);
  const subtotal = partTotals.subtotal;
  const changeItemTeil = (item: QuoteItem, teil: OfferPart) => {
    if (item.id.startsWith("lv-")) onLvTeilChange?.(item.id.slice(3), teil);
    else setItems((previous) => previous.map((row) => row.id === item.id ? { ...row, teil } : row));
  };
  const partPreviewRows = [
    ...mergedItems.map((item) => ({ ...item, onTeilChange: (teil: OfferPart) => changeItemTeil(item, teil) })),
    ...(hasPerPhotoBreakdown ? perPhotoBreakdown.flatMap((photo, photoIndex) => photo.positions.map((position, index) => ({
      id: `photo-${photoIndex}-${index}`, description: position.beschreibung?.trim() || position.name,
      quantity: position.qty, unit: position.unit, unitPrice: position.ep, total: position.total,
      teil: position.teil, variant: position.variant, sourceLabel: `${photo.photoLabel}${photo.fileName ? ` · ${photo.fileName}` : ""}`,
      onTeilChange: (teil: OfferPart) => {
        if (photo.photoId && position.sourceIndex !== undefined) onPhotoTeilChange?.(photo.photoId, position.sourceIndex, teil);
      },
    }))) : []),
  ];
  const taxRate = 0.20; // 20% MwSt
  const tax = subtotal * taxRate;
  const total = subtotal + tax;

  // ────────────────────────────────────────────────────────────────────────
  // PLAUSIBILITÄTSPRÜFUNG
  // Eingaben für die Prüfung sammeln aus:
  //  - Aktiven LV-Positionen (qty > 0, !variant)
  //  - Manuellen Zusatzpositionen (items ohne lv-/pp-agg-Präfix)
  //  - Foto-aggregierten Positionen (sowohl perPhotoBreakdown als auch das
  //    Fallback aus aggregatedPhotoItems, je nach Modus). Varianten werden
  //    übersprungen, da sie nicht in die Summe einfließen.
  // ────────────────────────────────────────────────────────────────────────
  const { data: libraryPositions = [] } = useQuery<LibraryPosition[]>({
    queryKey: ["/api/library-positions"],
  });

  const checkPositions = useMemo<CheckPosition[]>(() => {
    const list: CheckPosition[] = [];
    // 1) LV-Hauptpositionen (Wahrheit kommt aus lvPositionen)
    lvPositionen.forEach((p) => {
      if (p.variant) return;
      if (p.qty <= 0 && p.ep <= 0) return;
      list.push({
        key: `lv-${p.id}`,
        source: "lv",
        lvPositionId: p.id,
        name: p.name,
        unit: p.unit,
        ep: p.ep,
        qty: p.qty,
      });
    });
    // 2) Manuelle Zusatzpositionen aus dem QuoteGenerator-State
    items.forEach((it) => {
      if (it.id.startsWith("lv-")) return;
      if (it.id.startsWith("pp-agg-")) return;
      if (it.variant) return;
      if (!it.description) return;
      list.push({
        key: `manual-${it.id}`,
        source: "manual",
        name: it.description,
        unit: it.unit,
        ep: it.unitPrice,
        qty: it.quantity,
      });
    });
    // 3) Foto-Mengen
    if (hasPerPhotoBreakdown) {
      perPhotoBreakdown.forEach((entry, eIdx) => {
        entry.positions.forEach((pos, pIdx) => {
          if (pos.variant) return;
          if (pos.qty <= 0 && pos.ep <= 0) return;
          list.push({
            key: `pp-${eIdx}-${pos.posId ?? pIdx}`,
            source: "photo-agg",
            lvPositionId: pos.posId,
            name: pos.name,
            unit: pos.unit,
            ep: pos.ep,
            qty: pos.qty,
          });
        });
      });
    } else {
      photoAggregateItems.forEach((it) => {
        list.push({
          key: it.id,
          source: "photo-agg",
          lvPositionId: it.id.startsWith("pp-agg-") ? it.id.slice(7) : undefined,
          name: it.description,
          unit: it.unit,
          ep: it.unitPrice,
          qty: it.quantity,
        });
      });
    }
    return list;
  }, [lvPositionen, items, hasPerPhotoBreakdown, perPhotoBreakdown, photoAggregateItems]);

  const plausibilityFindings = useMemo<PlausibilityFinding[]>(
    () => runPlausibilityChecks(checkPositions, libraryPositions),
    [checkPositions, libraryPositions],
  );
  const plausibilitySummary = useMemo(
    () => summarizeFindings(plausibilityFindings),
    [plausibilityFindings],
  );
  const [plausibilityExpanded, setPlausibilityExpanded] = useState(true);
  const [plausibilityConfirmOpen, setPlausibilityConfirmOpen] = useState(false);
  // Aufgeklappte Positions-Karten innerhalb des Plausi-Blocks. Nutzt die
  // LV-Position-ID als Schlüssel; Sammelgruppe „Allgemeine Hinweise" verwendet
  // den Spezial-Key PLAUSI_ORPHAN_KEY. Wird hier (statt im Block) verwaltet,
  // damit die Eltern-Komponente die aktuell aufgeklappten Karten beim Sprung
  // ins LV mitnehmen und beim Rücksprung wiederherstellen kann.
  const [plausibilityExpandedPositions, setPlausibilityExpandedPositions] = useState<Set<string>>(
    () => new Set<string>(),
  );

  // Restore-Hint anwenden: Block aufklappen, Karten-Set wiederherstellen,
  // an die Ziel-Karte scrollen und das Eltern-State zurücksetzen.
  useEffect(() => {
    if (!plausibilityRestoreHint) return;
    setPlausibilityExpanded(true);
    const restored = new Set(plausibilityRestoreHint.expandedPositionIds);
    restored.add(plausibilityRestoreHint.posId);
    setPlausibilityExpandedPositions(restored);
    const targetId = plausibilityRestoreHint.posId;
    const timer = setTimeout(() => {
      const el = document.querySelector(
        `[data-plausibility-pos-card="${CSS.escape(targetId)}"]`,
      );
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (onPlausibilityRestoreConsumed) onPlausibilityRestoreConsumed();
    }, 120);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plausibilityRestoreHint]);

  // Befunde nach LV-Position-ID gruppieren, damit der LV-Kalkulator pro Zeile
  // ein Warn-Symbol mit Tooltip anzeigen kann. Wir reichen ausschließlich
  // Befunde durch, die einer konkreten LV-Position zuordenbar sind (Befunde
  // ohne `lvPositionId` — z. B. rein manuelle/foto-aggregierte Positionen —
  // bleiben weiterhin nur im Plausi-Block sichtbar). Bei mehreren Positionen
  // pro Befund (z. B. „Ähnliche Positionen mit abweichendem EP") wird der
  // Titel jeder beteiligten LV-Position zugerechnet, damit dort ebenfalls ein
  // Indikator erscheint.
  const plausibilityFindingsByPosId = useMemo<
    Record<string, { count: number; titles: string[] }>
  >(() => {
    const map: Record<string, { count: number; titles: string[] }> = {};
    plausibilityFindings.forEach((finding) => {
      const seenForFinding = new Set<string>();
      finding.positions.forEach((p) => {
        const id = p.lvPositionId;
        if (!id) return;
        if (seenForFinding.has(id)) return;
        seenForFinding.add(id);
        const entry = map[id] ?? { count: 0, titles: [] };
        entry.count += 1;
        if (!entry.titles.includes(finding.title)) {
          entry.titles.push(finding.title);
        }
        map[id] = entry;
      });
    });
    return map;
  }, [plausibilityFindings]);

  // Map an die Eltern-Komponente weitergeben. Stabile JSON-Vergleichsstrategie,
  // damit identische Befund-Resultate keine Re-Renders im Eltern auslösen
  // (potenzielle Save-Loop-Quelle, vgl. Kommentare zur Persistenz-Bridge).
  const lastEmittedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!onPlausibilityFindingsByPosIdChange) return;
    const serialized = JSON.stringify(plausibilityFindingsByPosId);
    if (lastEmittedRef.current === serialized) return;
    lastEmittedRef.current = serialized;
    onPlausibilityFindingsByPosIdChange(plausibilityFindingsByPosId);
  }, [plausibilityFindingsByPosId, onPlausibilityFindingsByPosIdChange]);

  /**
   * Baut das QuoteData-Objekt aus den aktuellen Komponentendaten zusammen.
   * Wird sowohl für PDF-Download als auch für E-Mail-Versand verwendet.
   */
  const buildQuoteData = (): QuoteData => ({
    projectName: projectName || 'Bauprojekt',
    customerName: customerData.name,
    customerAddress: customerData.address,
    quoteNumber,
    quoteDate,
    items: mergedItems.map(item => {
      const isLv = item.id.startsWith('lv-');
      const isPhotoAgg = item.id.startsWith('pp-agg-');
      const posId = isLv ? item.id.slice(3) : null;
      const mengenRef = (posId && showMeasurementSources) ? lvMengenRefs[posId] : null;
      const sourceLabel = mengenRef?.sourceLabel
        ?? (isPhotoAgg ? item.sourceLabel : undefined);
      return {
        description: item.description,
        quantity: item.quantity,
        unit: item.unit,
        unitPrice: item.unitPrice,
        total: item.total,
        teil: item.teil,
        ...(sourceLabel ? { sourceLabel } : {}),
        ...(item.variant ? { variant: true } : {}),
        ...(item.beschreibung ? { beschreibung: item.beschreibung } : {}),
      };
    }),
    photos: nonMeasurementPhotos.filter(p => selectedPhotoIds.has(p.id)),
    measurements: measurementPdfData,
    perPhoto: perPhotoBreakdown.length > 0 ? perPhotoBreakdown : undefined,
    offerParts,
    notes,
    validUntil,
    subtotal,
    tax,
    total,
    projectDetails: projectDetails && (
      projectDetails.activity ||
      (projectDetails.objektbeschaffenheit?.length ?? 0) > 0 ||
      (projectDetails.massnahmen?.length ?? 0) > 0 ||
      projectDetails.geschosszahl != null ||
      projectDetails.hoehe != null ||
      projectDetails.menge != null ||
      (projectDescription ?? '').trim().length > 0
    ) ? { ...projectDetails, description: projectDescription ?? undefined } : (
      (projectDescription ?? '').trim().length > 0
        ? { description: projectDescription ?? undefined }
        : undefined
    ),
  });

  // ────────────────────────────────────────────────────────────────────────
  // Live-Vorschau: erzeugt das PDF in-memory und rendert es als Blob-URL in
  // einem eingebetteten <iframe>. Bei jeder relevanten Änderung wird die
  // Vorschau mit kurzer Entprellung neu aufgebaut, alte Blob-URLs werden
  // sauber freigegeben.
  // ────────────────────────────────────────────────────────────────────────
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [showLayoutPanel, setShowLayoutPanel] = useState(false);
  const previewUrlRef = useRef<string | null>(null);

  const previewDepKey = JSON.stringify({
    offerParts,
    customer: customerData,
    project: projectName,
    projectDescription: projectDescription ?? '',
    notes,
    validUntil,
    quoteDate,
    quoteNumber,
    items: mergedItems.map(i => ({
      id: i.id, teil: i.teil, d: i.description, q: i.quantity, u: i.unit, p: i.unitPrice, s: i.sourceLabel, v: !!i.variant, b: i.beschreibung ?? null,
    })),
    selectedPhotoIds: Array.from(selectedPhotoIds).sort(),
    measurements: measurementResults
      ? Array.from(measurementResults.entries())
          .map(([id, r]) => ({
            id,
            a: getMeasurementAmount(r),
            u: getMeasurementUnit(r),
          }))
          .sort((a, b) => a.id.localeCompare(b.id))
      : [],
    perPhoto: perPhotoBreakdown.map(e => ({
      l: e.photoLabel,
      f: e.fileName,
      a: e.amount,
      u: e.unit,
      // Preview-Identität (URL-Stamm) im Dep-Key, damit die Live-Vorschau
      // auch dann neu rendert, wenn ein Foto-Vorschaubild später verfügbar wird
      // oder sich ändert (z.B. Wechsel auf annotierte Variante).
      pv: e.preview ?? null,
      s: e.subtotal,
      p: e.positions.map(p => ({
        i: p.posId, teil: p.teil, v: p.variant, n: p.name, q: p.qty, u: p.unit, e: p.ep, t: p.total,
      })),
    })),
    lvRefs: lvMengenRefs,
    showMeasurementSources,
    pdfLayout,
  });

  // Pflicht-Validierung der Leistungsbeschreibung. Ohne Beschreibung darf
  // weder die Live-Vorschau erzeugt noch ein PDF heruntergeladen oder per
  // E-Mail versendet werden — der Nutzer wird auf das Eingabefeld in
  // Schritt 1 zurückverwiesen.
  const projectDescriptionMissing = (projectDescription ?? '').trim().length === 0;

  useEffect(() => {
    if (!customerData.name.trim() || projectDescriptionMissing) {
      setPreviewError(null);
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
      setPreviewUrl(null);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(async () => {
      setPreviewLoading(true);
      setPreviewError(null);
      try {
        const generator = new PDFGenerator();
        const data = buildQuoteData();
        const blob = await generator.generateQuotePDF(data, pdfLayout);
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = url;
        setPreviewUrl(url);
      } catch (err) {
        if (cancelled) return;
        console.error('Vorschau-Fehler:', err);
        setPreviewError('Vorschau konnte nicht erstellt werden.');
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewDepKey]);

  useEffect(() => () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  }, []);

  /**
   * Erzeugt das PDF tatsächlich (ohne Plausibilitäts-Gate). Wird sowohl
   * direkt aus dem Button als auch aus der Bestätigungs-Abfrage gerufen,
   * wenn Plausibilitätshinweise vorliegen.
   */
  const runGeneratePDF = async (action: "download" | "preview" = "download", variant: QuotePdfVariant = "gegliedert") => {
    if (pdfGenerationInFlightRef.current) return;
    pdfGenerationInFlightRef.current = true;
    setIsGeneratingPDF(true);

    try {
      const quoteData = buildQuoteData();

      const pdfBlob = await pdfGenerator.generateQuotePDF({ ...quoteData, pdfExportVariant: variant }, pdfLayout);
      if (!pdfDialogMountedRef.current) return;

      // PDF Download
      const url = URL.createObjectURL(pdfBlob);
      const filename = quotePdfFilename(quoteData.quoteNumber ?? "", variant);

      if (pdfDialogUrlRef.current) URL.revokeObjectURL(pdfDialogUrlRef.current);
      pdfDialogUrlRef.current = url;
      const document = { url, filename };
      setPdfDialog(document);
      if (action === "download") downloadDialogPdf(document);

    } catch (error) {
      console.error('Fehler bei PDF-Generierung:', error);
      toast({
        title: "PDF Fehler",
        description: "Das PDF konnte nicht erstellt werden. Versuchen Sie es erneut.",
        variant: "destructive"
      });
    } finally {
      pdfGenerationInFlightRef.current = false;
      if (pdfDialogMountedRef.current) setIsGeneratingPDF(false);
    }
  };

  /**
   * Generiert PDF-Angebot mit echten Daten. Bei vorhandenen Plausibilitäts-
   * Hinweisen wird vorher eine Bestätigungs-Abfrage angezeigt.
   */
  const generatePDF = async (action: "download" | "preview" = "download") => {
    pdfActionRef.current = action;
    pendingPdfActionRef.current = action;
    if (!customerData.name.trim()) {
      toast({
        title: "Kundendaten fehlen",
        description: "Bitte geben Sie mindestens den Kundennamen ein.",
        variant: "destructive"
      });
      return;
    }
    if (projectDescriptionMissing) {
      toast({
        title: "Leistungsbeschreibung fehlt",
        description: "Bitte Leistungsbeschreibung eintragen, bevor das Angebot erstellt werden kann.",
        variant: "destructive",
      });
      onScrollToProjectDescription?.();
      return;
    }
    if (partTotals.grouped) {
      setPdfVariantDraft(pdfExportVariant);
      setPdfVariantDialogOpen(true);
      return;
    }
    pdfVariantRef.current = "gegliedert";
    if (plausibilityFindings.length > 0) {
      // Nicht blockierend — der Nutzer kann trotzdem fortfahren.
      setPlausibilityConfirmOpen(true);
      return;
    }
    await runGeneratePDF(action);
  };

  /**
   * Sendet Angebot per E-Mail
   */
  const sendQuoteEmail = async (variant?: QuotePdfVariant) => {
    if (!customerData.email.trim()) {
      toast({
        title: "E-Mail Adresse fehlen",
        description: "Bitte geben Sie die E-Mail Adresse des Kunden ein.",
        variant: "destructive"
      });
      return;
    }
    if (projectDescriptionMissing) {
      toast({
        title: "Leistungsbeschreibung fehlt",
        description: "Bitte Leistungsbeschreibung eintragen, bevor das Angebot erstellt werden kann.",
        variant: "destructive",
      });
      onScrollToProjectDescription?.();
      return;
    }

    if (!variant && partTotals.grouped) {
      pendingPdfActionRef.current = "email";
      setPdfVariantDraft(pdfExportVariant);
      setPdfVariantDialogOpen(true);
      return;
    }
    setIsSendingEmail(true);

    try {
      // Erst PDF generieren
      const quoteData = buildQuoteData();

      const pdfBlob = await pdfGenerator.generateQuotePDF({ ...quoteData, pdfExportVariant: variant ?? "gegliedert" }, pdfLayout);
      
      // E-Mail Template verwenden
      const emailTemplate = EmailTemplates.quote(customerData.name, projectName || 'Ihr Projekt');
      
      // Aufmaß-Fotos als separate Anhänge sammeln (werden im EmailService verkleinert)
      const photoAttachments: Blob[] = [];
      if (measurementResults && measurementResults.size > 0) {
        measurementResults.forEach((_result, photoId) => {
          const photo = photos.find((p) => p.id === photoId);
          if (photo?.file) {
            photoAttachments.push(photo.file);
          }
        });
      }

      const emailData = {
        to: customerData.email,
        subject: emailTemplate.subject,
        message: emailTemplate.message,
        attachments: [pdfBlob],
        photoAttachments,
        customerName: customerData.name,
        projectName: projectName
      };

      const success = await emailService.sendQuoteEmail(emailData);
      
      if (success) {
        toast({
          title: "E-Mail gesendet",
          description: `Angebot wurde an ${customerData.email} gesendet.`
        });
      } else {
        toast({
          title: "E-Mail Client geöffnet",
          description: "E-Mail wurde im Standard-Client vorbereitet (PDF separat speichern)."
        });
      }

    } catch (error) {
      console.error('Fehler beim E-Mail Versand:', error);
      toast({
        title: "E-Mail Fehler", 
        description: "Die E-Mail konnte nicht gesendet werden.",
        variant: "destructive"
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  const offerTemplateSections = assembleOfferTemplateSections({
    items: mergedItems,
    photos: hasPerPhotoBreakdown ? perPhotoBreakdown : [],
    lvPositions: lvPositionen,
    calculations: projectCalculations,
    sectionTitles: projectSectionTitles,
    groups: STAMMDATEN.map((section) => ({
      id: section.id, title: section.titel,
      positionIds: [...section.positionen.map((position) => position.id),
        ...libraryPositions.filter((position) => position.sektionId === section.id).map((position) => `lib-${position.id}`)],
    })),
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-bauwerk-orange" />
            Angebotserstellung
          </CardTitle>
          <CardDescription>
            Erstellen Sie ein professionelles Angebot auf Basis Ihrer Schadensdokumentation
          </CardDescription>
          <div className="pt-2">
            <SaveOfferCalculationTemplate sections={offerTemplateSections} projectName={projectName} />
          </div>
        </CardHeader>
        <CardContent className="space-y-6">

          {/* Angebotsnummer + Angebotsdatum */}
          <div className="flex items-center justify-between bg-bauwerk-teal/5 border border-bauwerk-teal/20 rounded-lg px-4 py-2.5 gap-4 flex-wrap">
            <div className="flex items-center gap-2 text-sm text-muted-foreground min-w-0">
              <Building2 className="w-4 h-4 text-bauwerk-teal shrink-0" />
              <span className="shrink-0">Angebots-Nr.:</span>
              {quoteNumberEditing ? (
                <div className="flex items-center gap-1.5">
                  <Input
                    autoFocus
                    value={quoteNumberDraft}
                    onChange={(e) => setQuoteNumberDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const trimmed = quoteNumberDraft.trim();
                        if (trimmed) setQuoteNumber(trimmed);
                        setQuoteNumberEditing(false);
                      } else if (e.key === 'Escape') {
                        setQuoteNumberEditing(false);
                      }
                    }}
                    onBlur={() => {
                      const trimmed = quoteNumberDraft.trim();
                      if (trimmed) setQuoteNumber(trimmed);
                      setQuoteNumberEditing(false);
                    }}
                    className="h-7 py-0 px-2 text-sm font-semibold text-bauwerk-teal w-40"
                  />
                  <button
                    type="button"
                    className="text-bauwerk-teal hover:text-bauwerk-teal/80"
                    onClick={() => {
                      const trimmed = quoteNumberDraft.trim();
                      if (trimmed) setQuoteNumber(trimmed);
                      setQuoteNumberEditing(false);
                    }}
                  >
                    <Check className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-1.5">
                  <span className="font-semibold text-bauwerk-teal">{quoteNumber}</span>
                  <button
                    type="button"
                    title="Angebotsnummer bearbeiten"
                    className="text-muted-foreground hover:text-bauwerk-teal transition-colors"
                    onClick={() => {
                      setQuoteNumberDraft(quoteNumber);
                      setQuoteNumberEditing(true);
                    }}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground shrink-0">
              <span>Datum:</span>
              <input
                type="date"
                value={quoteDate}
                onChange={(e) => setQuoteDate(e.target.value)}
                className="h-7 rounded border border-input bg-background px-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-bauwerk-teal"
              />
            </div>
          </div>
          
          {/* Kundendaten */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="customerName">Kundenname *</Label>
              <Input
                id="customerName"
                value={customerData.name}
                onChange={(e) => setCustomerData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Max Mustermann"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="customerEmail">E-Mail Adresse</Label>
              <Input
                id="customerEmail"
                type="email"
                value={customerData.email}
                onChange={(e) => setCustomerData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="kunde@example.com"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="customerAddress">Kundenadresse</Label>
            <Textarea
              id="customerAddress"
              value={customerData.address}
              onChange={(e) => { setAddressManuallyEdited(true); setCustomerData(prev => ({ ...prev, address: e.target.value })); }}
              placeholder="Musterstraße 123&#10;1010 Wien&#10;Österreich"
              rows={3}
            />
          </div>

          {/* Projektbeschreibung — Stammdaten aus Schritt 1 + 2.
              Wird nur gerendert, wenn mindestens ein Feld gefüllt ist. */}
          {projectDetails && (
            projectDetails.activity ||
            (projectDetails.objektbeschaffenheit?.length ?? 0) > 0 ||
            (projectDetails.massnahmen?.length ?? 0) > 0 ||
            projectDetails.geschosszahl != null ||
            projectDetails.hoehe != null ||
            projectDetails.menge != null
          ) && (
            <div className="rounded-lg border border-bauwerk-mint bg-bauwerk-mint/10 p-4 space-y-2" data-testid="quote-project-details">
              {projectDetails.activity && (
                <div>
                  <h4 className="text-base font-bold text-bauwerk-teal" data-testid="quote-project-activity">
                    {formatActivityLabel(projectDetails.activity)}
                  </h4>
                  <div className="h-0.5 w-12 bg-bauwerk-orange mt-1 mb-2" />
                </div>
              )}
              <dl className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-x-4 gap-y-1.5 text-sm">
                {(projectDetails.objektbeschaffenheit?.length ?? 0) > 0 && (
                  <>
                    <dt className="text-muted-foreground">Objektbeschaffenheit:</dt>
                    <dd className="font-medium" data-testid="quote-project-objekt">
                      {capitalizeList(projectDetails.objektbeschaffenheit).join(', ')}
                    </dd>
                  </>
                )}
                {(projectDetails.massnahmen?.length ?? 0) > 0 && (
                  <>
                    <dt className="text-muted-foreground">Maßnahmen:</dt>
                    <dd className="font-medium" data-testid="quote-project-massnahmen">
                      {capitalizeList(projectDetails.massnahmen).join(', ')}
                    </dd>
                  </>
                )}
                {projectDetails.geschosszahl != null && (
                  <>
                    <dt className="text-muted-foreground">Geschosszahl:</dt>
                    <dd className="font-medium" data-testid="quote-project-geschoss">{projectDetails.geschosszahl}</dd>
                  </>
                )}
                {projectDetails.hoehe != null && (
                  <>
                    <dt className="text-muted-foreground">Höhe:</dt>
                    <dd className="font-medium" data-testid="quote-project-hoehe">
                      {formatDecimal(projectDetails.hoehe)} m
                    </dd>
                  </>
                )}
                {projectDetails.menge != null && (
                  <>
                    <dt className="text-muted-foreground">Mengenausmaß:</dt>
                    <dd className="font-medium" data-testid="quote-project-menge">
                      {formatDecimal(projectDetails.menge)}
                      {projectDetails.mengeneinheit ? ` ${projectDetails.mengeneinheit}` : ''}
                    </dd>
                  </>
                )}
              </dl>
            </div>
          )}

          <Separator />

          {/* Flächenmessung Anzeige */}
          {measurementResults && measurementResults.size > 0 && (
            <div className="space-y-4">
              <h4 className="font-semibold text-bauwerk-teal">Flächenmessungen</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Array.from(measurementResults.entries()).map(([photoId, result]) => {
                  const photoIndex = photos.findIndex(p => p.id === photoId);
                  const photo = photos[photoIndex];
                  const photoName = `Foto ${photoIndex + 1}`;

                  return (
                    <Card key={photoId} className="p-4" data-testid={`aufmass-card-${photoId}`}>
                      <div className="space-y-3">
                        {photo?.preview && (
                          <QuoteImageButton
                            src={photo.preview}
                            alt={photoName}
                            className="w-full h-40 object-cover rounded-md border"
                            imageTestId={`aufmass-image-${photoId}`}
                            onOpen={setLightboxImage}
                          />
                        )}
                        <div className="flex items-center gap-2">
                          <Calculator className="w-4 h-4 text-bauwerk-orange" />
                          <span className="font-medium">{photoName}</span>
                          {(photo?.displayName?.trim() || photo?.file?.name) && (
                            <span className="text-xs text-muted-foreground truncate">
                              ({photo?.displayName?.trim() || photo?.file?.name})
                            </span>
                          )}
                        </div>
                        <div className="text-sm space-y-1">
                          <div className="flex justify-between">
                            <span>Menge:</span>
                            <span className="font-medium text-bauwerk-orange">
                              {getMeasurementAmount(result).toFixed(2).replace('.', ',')} {getMeasurementUnit(result)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
              
              {(() => {
                const m2Sum = Array.from(measurementResults.values())
                  .filter((r) => getMeasurementUnit(r) === 'm²')
                  .reduce((sum, r) => sum + getMeasurementAmount(r), 0);
                if (m2Sum <= 0) return null;
                return (
                  <div className="bg-bauwerk-orange/10 p-3 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Gesamte Arbeitsfläche (m²):</span>
                      <span className="text-lg font-bold text-bauwerk-orange">
                        {formatArea(m2Sum)}
                      </span>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Projektfotos im PDF */}
          {nonMeasurementPhotos.length > 0 && (
            <>
              <Separator />
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-bauwerk-teal flex items-center gap-2">
                    <Image className="w-4 h-4" />
                    Projektfotos im PDF
                  </h4>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedPhotoIds(new Set(nonMeasurementPhotos.map(p => p.id)))}
                    >
                      Alle
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedPhotoIds(new Set())}
                    >
                      Keine
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  Wählen Sie aus, welche Fotos im PDF-Angebot erscheinen sollen. Aufmaß-Fotos sind immer im Aufmaß-Abschnitt enthalten.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {nonMeasurementPhotos.map((photo, index) => {
                    const isSelected = selectedPhotoIds.has(photo.id);
                    return (
                      <div
                        key={photo.id}
                        className={`relative rounded-lg border-2 cursor-pointer transition-all ${isSelected ? 'border-bauwerk-teal bg-bauwerk-teal/5' : 'border-gray-200 opacity-60'}`}
                        onClick={() => setSelectedPhotoIds(prev => {
                          const next = new Set(prev);
                          if (next.has(photo.id)) next.delete(photo.id);
                          else next.add(photo.id);
                          return next;
                        })}
                      >
                        {photo.preview ? (
                          <QuoteImageButton
                            src={photo.preview}
                            alt={`Foto ${index + 1}`}
                            className="w-full h-24 object-cover rounded-t-md"
                            onOpen={setLightboxImage}
                          />
                        ) : (
                          <div className="w-full h-24 bg-gray-100 rounded-t-md flex items-center justify-center">
                            <Image className="w-8 h-8 text-gray-300" />
                          </div>
                        )}
                        <div className="flex items-center gap-2 p-2">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => setSelectedPhotoIds(prev => {
                              const next = new Set(prev);
                              if (next.has(photo.id)) next.delete(photo.id);
                              else next.add(photo.id);
                              return next;
                            })}
                            className="h-4 w-4 accent-bauwerk-teal cursor-pointer"
                          />
                          <span className="text-xs truncate text-muted-foreground">
                            {photo.displayName?.trim() || photo.file?.name || `Foto ${index + 1}`}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground">
                  {selectedPhotoIds.size} von {nonMeasurementPhotos.length} Fotos ausgewählt
                </p>
              </div>
            </>
          )}

          <Separator />

          {/* Angebotspositionen */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-bauwerk-teal">Angebotspositionen</h4>
              <Button type="button" onClick={addItem} variant="outline" size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Position hinzufügen
              </Button>
            </div>

            <ReorderableQuotePositions items={mergedItems} onReorder={setPositionOrder}>
              {(item, index, controls) => {
                const isLv = item.id.startsWith('lv-');
                const isPhotoAgg = item.id.startsWith('pp-agg-');
                const isLocked = isLv || isPhotoAgg;
                const posId = isLv ? item.id.slice(3) : null;
                const mengenRef = (posId && showMeasurementSources) ? lvMengenRefs[posId] : null;
                return (
                <Card key={item.id} className={`p-4 ${isLv ? 'border-emerald-300 bg-emerald-50/40 dark:border-emerald-800 dark:bg-emerald-950/20' : ''} ${isPhotoAgg ? 'border-bauwerk-orange/50 bg-orange-50/40 dark:bg-orange-950/10' : ''}`}>
                  <div className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
                    Angebotsteil
                    <OfferPartSelect value={item.teil} onChange={(teil) => changeItemTeil(item, teil)} label={`Angebotsteil für ${item.description}`} />
                    {controls}
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-6 gap-4 items-end">
                    <div className="lg:col-span-2">
                      <div className="flex items-center gap-2 mb-1">
                        <Label>Beschreibung</Label>
                        {isLv && (
                          <Badge variant="secondary" className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200 text-[10px] px-1.5 py-0 h-5 flex items-center gap-1">
                            <Lock className="w-3 h-3" />
                            LV
                          </Badge>
                        )}
                        {isPhotoAgg && (
                          <Badge variant="secondary" className="bg-orange-100 text-orange-800 dark:bg-orange-900/60 dark:text-orange-200 text-[10px] px-1.5 py-0 h-5 flex items-center gap-1">
                            <Lock className="w-3 h-3" />
                            Foto-Menge
                          </Badge>
                        )}
                        {mengenRef && posId && (() => {
                          const mengeDetail = availableMengen.find(m => m.id === mengenRef.mengeId);
                          return (
                            <Popover
                              open={openPopoverItemId === item.id}
                              onOpenChange={(open) => setOpenPopoverItemId(open ? item.id : null)}
                            >
                              <PopoverTrigger asChild>
                                <Badge
                                  variant="outline"
                                  className={`text-[10px] px-1.5 py-0 h-5 flex items-center gap-1 text-emerald-700 border-emerald-300 bg-emerald-50 dark:text-emerald-300 dark:border-emerald-700 dark:bg-emerald-950/30 max-w-[180px] truncate cursor-pointer hover:bg-emerald-100 dark:hover:bg-emerald-900/50 transition-colors ${onScrollToLvPosition ? "cursor-pointer hover:bg-emerald-100 dark:hover:bg-emerald-900/50" : ""}`}
                                  title={onScrollToLvPosition ? `Zur LV-Position springen: ${mengenRef.sourceLabel}` : mengenRef.sourceLabel}
                                >
                                  <Link2 className="w-2.5 h-2.5 shrink-0" />
                                  <span className="truncate">{mengenRef.sourceLabel}</span>
                                </Badge>
                              </PopoverTrigger>
                              <PopoverContent className="w-64 p-3" align="start">
                                <div className="space-y-2">
                                  <div className="flex items-center gap-1.5 text-emerald-700 dark:text-emerald-300 font-medium text-xs">
                                    <Ruler className="w-3.5 h-3.5" />
                                    Aufmaß-Details
                                  </div>
                                  <div className="space-y-1.5 text-xs">
                                    {mengeDetail ? (
                                      <>
                                        {(() => {
                                          const photoPreview = photos[mengeDetail.imageIndex]?.preview;
                                          return photoPreview ? (
                                            <button
                                              type="button"
                                              aria-label={`${mengeDetail.imageName} vergrößern`}
                                              className="rounded overflow-hidden border border-border mb-2 cursor-zoom-in group relative"
                                              onClick={() => setLightboxImage({ src: photoPreview, alt: mengeDetail.imageName })}
                                              title="Klicken zum Vergrößern"
                                            >
                                              <img
                                                src={photoPreview}
                                                alt={mengeDetail.imageName}
                                                className="w-full h-24 object-cover group-hover:opacity-90 transition-opacity"
                                              />
                                              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                                <div className="bg-black/40 rounded-full p-1">
                                                  <Image className="w-4 h-4 text-white" />
                                                </div>
                                              </div>
                                            </button>
                                          ) : null;
                                        })()}
                                        <div className="flex items-start gap-1.5">
                                          <span className="text-muted-foreground shrink-0 w-14">Bild</span>
                                          <span className="font-medium break-all">{mengeDetail.imageName}</span>
                                        </div>
                                        <div className="flex items-start gap-1.5">
                                          <span className="text-muted-foreground shrink-0 w-14">Bezeichnung</span>
                                          <span className="font-medium">{mengeDetail.label}</span>
                                        </div>
                                        <div className="flex items-start gap-1.5">
                                          <span className="text-muted-foreground shrink-0 w-14">Wert</span>
                                          <span className="font-medium tabular-nums">
                                            {formatDecimal(mengeDetail.value, 2)} {mengeDetail.unit}
                                          </span>
                                        </div>
                                      </>
                                    ) : (
                                      <div className="flex items-start gap-1.5">
                                        <span className="text-muted-foreground shrink-0 w-14">Quelle</span>
                                        <span className="font-medium">{mengenRef.sourceLabel}</span>
                                      </div>
                                    )}
                                  </div>
                                  <div className="pt-1 border-t border-border">
                                    <button
                                      type="button"
                                      className="text-xs text-emerald-700 dark:text-emerald-300 hover:underline flex items-center gap-1"
                                      onClick={() => {
                                        setOpenPopoverItemId(null);
                                        if (onScrollToLvPosition && posId) {
                                          onScrollToLvPosition(posId);
                                        } else if (posId) {
                                          const el = document.querySelector(`[data-lv-position-id="${posId}"]`);
                                          if (el) {
                                            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                            el.classList.add('ring-2', 'ring-emerald-400', 'ring-offset-1');
                                            setTimeout(() => el.classList.remove('ring-2', 'ring-emerald-400', 'ring-offset-1'), 1800);
                                          }
                                        }
                                      }}
                                    >
                                      <Link2 className="w-3 h-3" />
                                      Zur Position springen
                                    </button>
                                  </div>
                                </div>
                              </PopoverContent>
                            </Popover>
                          );
                        })()}
                      </div>
                      <Input
                        value={item.description}
                        onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                        placeholder="Leistungsbeschreibung"
                        disabled={isLocked}
                        className={isLv ? 'bg-emerald-50/60 dark:bg-emerald-950/30' : isPhotoAgg ? 'bg-orange-50/60 dark:bg-orange-950/20' : ''}
                      />
                    </div>
                    <div>
                      <Label>Menge</Label>
                      <GermanNumberInput
                        value={item.quantity}
                        onValueChange={(n) => updateItem(item.id, 'quantity', n)}
                        min={0}
                        maxFractionDigits={2}
                        inputStyle
                        disabled={isLocked}
                        className={isLv ? 'bg-emerald-50/60 dark:bg-emerald-950/30' : isPhotoAgg ? 'bg-orange-50/60 dark:bg-orange-950/20' : ''}
                      />
                    </div>
                    <div>
                      <Label>Einheit</Label>
                      <Input
                        value={item.unit}
                        onChange={(e) => updateItem(item.id, 'unit', e.target.value)}
                        placeholder="m², Stk, h"
                        disabled={isLocked}
                        className={isLv ? 'bg-emerald-50/60 dark:bg-emerald-950/30' : isPhotoAgg ? 'bg-orange-50/60 dark:bg-orange-950/20' : ''}
                      />
                    </div>
                    <div>
                      <Label>Preis (€)</Label>
                      <GermanNumberInput
                        value={item.unitPrice}
                        onValueChange={(n) => updateItem(item.id, 'unitPrice', n)}
                        min={0}
                        maxFractionDigits={2}
                        minFractionDigits={2}
                        inputStyle
                        disabled={isLocked}
                        className={isLv ? 'bg-emerald-50/60 dark:bg-emerald-950/30' : isPhotoAgg ? 'bg-orange-50/60 dark:bg-orange-950/20' : ''}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right">
                        <Label>Gesamt</Label>
                        <div className="font-semibold">{formatAmount(item.total)} €</div>
                      </div>
                      {isLocked ? (
                        <Popover
                          open={openLockPopoverItemId === item.id}
                          onOpenChange={(open) => setOpenLockPopoverItemId(open ? item.id : null)}
                        >
                          <PopoverTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className={isLv
                                ? 'border-emerald-300 text-emerald-600 dark:border-emerald-700 dark:text-emerald-400'
                                : 'border-orange-300 text-orange-600 dark:border-orange-700 dark:text-orange-400'
                              }
                              title="Position bearbeiten oder entfernen"
                            >
                              <Lock className="w-4 h-4" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-56 p-2" align="end">
                            <div className="space-y-1">
                              <p className="text-xs text-muted-foreground px-2 py-1 font-medium">
                                {isLv ? 'LV-Position' : 'Foto-Position'}
                              </p>
                              <button
                                type="button"
                                className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-muted text-left transition-colors"
                                onClick={() => unlockItem(item.id, item)}
                              >
                                <LockOpen className="w-4 h-4 text-bauwerk-teal shrink-0" />
                                <span>Freigeben &amp; bearbeiten</span>
                              </button>
                              <button
                                type="button"
                                className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-red-50 dark:hover:bg-red-950/30 text-red-600 dark:text-red-400 text-left transition-colors"
                                onClick={() => removeItem(item.id)}
                              >
                                <Trash2 className="w-4 h-4 shrink-0" />
                                <span>Aus Angebot entfernen</span>
                              </button>
                            </div>
                          </PopoverContent>
                        </Popover>
                      ) : (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeItem(item.id)}
                          aria-label="Position entfernen"
                          title="Position entfernen"
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
                );
              }}
            </ReorderableQuotePositions>
            <div className="flex justify-end border-t pt-3">
              <Button type="button" onClick={addItem} variant="outline" size="sm" data-testid="button-add-position-bottom">
                <Plus className="w-4 h-4 mr-2" />Position hinzufügen
              </Button>
            </div>
          </div>

          <Separator />

          {/* Zusätzliche Angaben */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="validUntil">Gültig bis</Label>
                <Input
                  id="validUntil"
                  type="date"
                  value={validUntil}
                  onChange={(e) => setValidUntil(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Zusätzliche Hinweise</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Besondere Bedingungen, Zahlungsmodalitäten, etc."
                rows={3}
              />
            </div>
          </div>

          <Separator />

          {/* Preisübersicht */}
          <section className="space-y-3 rounded-lg border p-4">
            <h4 className="font-semibold text-bauwerk-teal">Angebotsteile A / B / C</h4>
            <p className="text-xs text-muted-foreground">Überschriften und Beschreibungstexte frei anpassen. Die Texte werden für dieses Projekt gespeichert und in Vorschau und gegliedertes PDF übernommen.</p>
            {OFFER_PARTS.map((part) => <div key={part} className="space-y-1">
              <Label htmlFor={`offer-part-title-${part}`}>Titel Teil {part}</Label>
              <Input id={`offer-part-title-${part}`} maxLength={150} value={offerParts[part].title}
                onChange={(event) => setOfferParts((previous) => ({ ...previous, [part]: { ...previous[part], title: event.target.value } }))} />
              <Label htmlFor={`offer-part-description-${part}`}>Beschreibung Teil {part}</Label>
              <Textarea id={`offer-part-description-${part}`} rows={3} value={offerParts[part].description ?? ""}
                placeholder={`Eigener Beschreibungstext für Teil ${part} (optional)`}
                onChange={(event) => setOfferParts((previous) => ({ ...previous, [part]: { ...previous[part], description: event.target.value } }))} />
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={offerParts[part].included} aria-label={`Teil ${part} in Gesamtsumme einrechnen`}
                  onChange={(event) => setOfferParts((previous) => ({ ...previous, [part]: { ...previous[part], included: event.target.checked } }))} />
                In Gesamtsumme einrechnen
              </label>
            </div>)}
            {perPhotoBreakdown.map((photo, index) => <div key={photo.photoId ?? index} className="flex items-center justify-between gap-2 border-t pt-2 text-xs">
              <span>{photo.photoLabel} · Alle Positionen dieses Fotos → Teil …</span>
              <select className="rounded border bg-background p-1" aria-label={`Alle Positionen ${photo.photoLabel} zuordnen`} value=""
                onChange={(event) => { if (photo.photoId && event.target.value) onPhotoTeilChange?.(photo.photoId, null, event.target.value as OfferPart); }}>
                <option value="">Teil wählen</option>{OFFER_PARTS.map((part) => <option key={part} value={part}>{part}</option>)}
              </select>
            </div>)}
          </section>
          <div className="bg-bauwerk-mint/10 p-4 rounded-lg">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Nettobetrag:</span>
                <span className="font-medium">{formatAmount(subtotal)} €</span>
              </div>
              {variantSubtotal > 0 && (
                <div className="flex justify-between text-amber-700">
                  <span>Summe der Variantenleistungen:</span>
                  <span className="font-medium">({formatAmount(variantSubtotal)} €)</span>
                </div>
              )}
              <div className="flex justify-between">
                <span>MwSt. (20%):</span>
                <span className="font-medium">{formatAmount(tax)} €</span>
              </div>
              <Separator />
              <div className="flex justify-between text-lg font-bold text-bauwerk-teal">
                <span>Gesamtbetrag:</span>
                <span>{formatAmount(total)} €</span>
              </div>
            </div>
          </div>

          {/* Angebotsvorschau */}
          <div className="space-y-2">
            <h4 className="font-semibold text-bauwerk-teal flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Angebotsvorschau
            </h4>
            <div className="border-2 border-bauwerk-teal/20 rounded-xl overflow-hidden shadow-sm">
              {/* Header bar */}
              <div className="bg-bauwerk-teal text-white flex items-center px-4 py-3">
                <div>
                  <p className="text-base font-bold leading-tight">{COMPANY.name}</p>
                  <p className="text-xs opacity-75 mt-0.5">{COMPANY.tagline}</p>
                </div>
              </div>

              {/* Company contact bar */}
              <div className="bg-gray-50 border-b border-gray-200 text-center px-4 py-1.5 space-y-0.5">
                {getCompanyContactLines().map((line, idx) => (
                  <p key={idx} className="text-[10px] text-gray-500">{line}</p>
                ))}
              </div>

              {/* Address + Info */}
              <div className="px-4 py-3 grid grid-cols-2 gap-4 border-b border-gray-100">
                <div>
                  <p className="text-[9px] uppercase tracking-wider text-gray-400 mb-1">An</p>
                  <p className="font-semibold text-sm">{customerData.name || '—'}</p>
                  {customerData.address && (
                    <p className="text-xs text-gray-500 whitespace-pre-line mt-0.5 leading-relaxed">
                      {customerData.address}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-[9px] uppercase tracking-wider text-gray-400 mb-1">Angebotsinformationen</p>
                  <p className="text-xs"><span className="text-gray-400">Nr.:</span> <span className="font-semibold text-bauwerk-teal">{quoteNumber}</span></p>
                  <p className="text-xs mt-0.5"><span className="text-gray-400">Datum:</span> {quoteDate ? new Date(quoteDate + 'T12:00:00').toLocaleDateString('de-DE') : new Date().toLocaleDateString('de-DE')}</p>
                  <p className="text-xs mt-0.5"><span className="text-gray-400">Gültig bis:</span> {validUntil ? new Date(validUntil).toLocaleDateString('de-DE') : '—'}</p>
                  {projectName && (
                    <p className="text-xs mt-0.5"><span className="text-gray-400">Projekt:</span> {projectName}</p>
                  )}
                </div>
              </div>

              {/* Projektbeschreibung (Schritt 1) — wird zwischen Adress-/Info-Block
                  und ANGEBOT-Tabelle gerendert, damit On-Screen-Vorschau und PDF
                  zusammenpassen. Bei leerem Text gar nichts anzeigen. */}
              {(projectDescription ?? '').trim().length > 0 && (
                <div className="px-4 pt-3 pb-2 border-b border-gray-100" data-testid="quote-preview-projectdescription">
                  <p className="text-[9px] uppercase tracking-wider text-gray-400 mb-1">Leistungsbeschreibung</p>
                  <FormattedDescription text={projectDescription ?? ""} />
                </div>
              )}

              {/* ANGEBOT title */}
              <div className="px-4 pt-3 pb-1">
                <h2 className="text-lg font-bold text-bauwerk-teal tracking-wide border-b-2 border-bauwerk-orange inline-block pb-0.5">
                  ANGEBOT
                </h2>
              </div>

              {/* Items table */}
              {partTotals.grouped && <OfferPartsPreview rows={partPreviewRows} settings={offerParts} />}
              {!partTotals.grouped && partTotals.parts.length > 0 && offerParts.A.description?.trim() &&
                <p className="whitespace-pre-wrap break-words px-4 py-2 text-xs text-muted-foreground">{offerParts.A.description}</p>}
              {!partTotals.grouped && partTotals.parts.some((part) => !part.included) &&
                <p className="p-4 text-xs font-medium text-amber-800">{OPTIONAL_PART_NOTE}</p>}
              <div className={partTotals.grouped ? "hidden" : ""}>
              <div className="px-4 pb-3 overflow-x-auto">
                <table className="w-full text-xs min-w-[500px]">
                  <thead>
                    <tr className="bg-bauwerk-teal text-white">
                      <th className="text-left px-2 py-1.5 rounded-tl font-medium w-7">#</th>
                      <th className="text-left px-2 py-1.5 font-medium">Beschreibung</th>
                      <th className="text-right px-2 py-1.5 font-medium w-16">Menge</th>
                      <th className="text-left px-2 py-1.5 font-medium w-16">Einheit</th>
                      <th className="text-right px-2 py-1.5 font-medium w-20">EP (€)</th>
                      <th className="text-right px-2 py-1.5 rounded-tr font-medium w-20">Gesamt (€)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mergedItems.map((item, i) => {
                      const isLvItem = item.id.startsWith('lv-');
                      const isPhotoAggItem = item.id.startsWith('pp-agg-');
                      const previewPosId = isLvItem ? item.id.slice(3) : null;
                      const previewMengenRef = (previewPosId && showMeasurementSources) ? lvMengenRefs[previewPosId] : null;
                      const previewMengeDetail = previewMengenRef ? availableMengen.find(m => m.id === previewMengenRef.mengeId) : null;
                      return (
                        <tr key={item.id} className={i % 2 === 1 ? 'bg-gray-50' : 'bg-white'}>
                          <td className="px-2 py-1 text-gray-400">{i + 1}</td>
                          <td className="px-2 py-1 text-gray-800 max-w-[180px]">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <span className="truncate">{item.description || '—'}</span>
                              {isPhotoAggItem && (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 flex items-center gap-1 text-orange-700 border-orange-300 bg-orange-50 dark:text-orange-300 dark:border-orange-700 dark:bg-orange-950/30 shrink-0">
                                  <Link2 className="w-2.5 h-2.5 shrink-0" />
                                  Aus Foto-Mengen
                                </Badge>
                              )}
                              {previewMengenRef && (
                                <Popover
                                  open={openPreviewPopoverItemId === item.id}
                                  onOpenChange={(open) => setOpenPreviewPopoverItemId(open ? item.id : null)}
                                >
                                  <PopoverTrigger asChild>
                                    <Badge
                                      variant="outline"
                                      className="text-[10px] px-1.5 py-0 h-5 flex items-center gap-1 text-emerald-700 border-emerald-300 bg-emerald-50 dark:text-emerald-300 dark:border-emerald-700 dark:bg-emerald-950/30 max-w-[120px] shrink-0 cursor-pointer hover:bg-emerald-100 dark:hover:bg-emerald-900/50 transition-colors"
                                      title={previewMengenRef.sourceLabel}
                                    >
                                      <Link2 className="w-2.5 h-2.5 shrink-0" />
                                      <span className="truncate">{previewMengenRef.sourceLabel}</span>
                                    </Badge>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-64 p-3" align="start">
                                    <div className="space-y-2">
                                      <div className="flex items-center gap-1.5 text-emerald-700 dark:text-emerald-300 font-medium text-xs">
                                        <Ruler className="w-3.5 h-3.5" />
                                        Aufmaß-Details
                                      </div>
                                      <div className="space-y-1.5 text-xs">
                                        {previewMengeDetail ? (
                                          <>
                                            <div className="flex items-start gap-1.5">
                                              <span className="text-muted-foreground shrink-0 w-14">Bild</span>
                                              <span className="font-medium break-all">{previewMengeDetail.imageName}</span>
                                            </div>
                                            <div className="flex items-start gap-1.5">
                                              <span className="text-muted-foreground shrink-0 w-14">Bezeichnung</span>
                                              <span className="font-medium">{previewMengeDetail.label}</span>
                                            </div>
                                            <div className="flex items-start gap-1.5">
                                              <span className="text-muted-foreground shrink-0 w-14">Wert</span>
                                              <span className="font-medium tabular-nums">
                                                {formatDecimal(previewMengeDetail.value, 2)} {previewMengeDetail.unit}
                                              </span>
                                            </div>
                                          </>
                                        ) : (
                                          <div className="flex items-start gap-1.5">
                                            <span className="text-muted-foreground shrink-0 w-14">Quelle</span>
                                            <span className="font-medium">{previewMengenRef.sourceLabel}</span>
                                          </div>
                                        )}
                                      </div>
                                      <div className="pt-1 border-t border-border">
                                        <button
                                          type="button"
                                          className="text-xs text-emerald-700 dark:text-emerald-300 hover:underline flex items-center gap-1"
                                          onClick={() => {
                                            setOpenPreviewPopoverItemId(null);
                                            if (onScrollToLvPosition && previewPosId) {
                                              onScrollToLvPosition(previewPosId);
                                            } else if (previewPosId) {
                                              const el = document.querySelector(`[data-lv-position-id="${previewPosId}"]`);
                                              if (el) {
                                                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                el.classList.add('ring-2', 'ring-emerald-400', 'ring-offset-1');
                                                setTimeout(() => el.classList.remove('ring-2', 'ring-emerald-400', 'ring-offset-1'), 1800);
                                              }
                                            }
                                          }}
                                        >
                                          <Link2 className="w-3 h-3" />
                                          Zur Position springen
                                        </button>
                                      </div>
                                    </div>
                                  </PopoverContent>
                                </Popover>
                              )}
                            </div>
                          </td>
                          <td className="px-2 py-1 text-right text-gray-700">
                            {formatDecimal(item.quantity, 2, { minFractionDigits: 0 })}
                          </td>
                          <td className="px-2 py-1 text-gray-600">{item.unit}</td>
                          <td className="px-2 py-1 text-right text-gray-700">{formatAmount(item.unitPrice)}</td>
                          <td className={`px-2 py-1 text-right font-semibold ${item.variant ? 'text-amber-700 italic' : 'text-gray-900'}`}>
                            {item.variant ? `(${formatAmount(item.total)})` : formatAmount(item.total)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="px-4 pb-4">
                <div className="ml-auto max-w-[280px] bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
                  <div className="flex justify-between items-center px-4 py-2 border-b border-gray-200">
                    <span className="text-xs text-gray-600">Nettobetrag</span>
                    <span className="text-xs font-medium">{formatAmount(subtotal)} €</span>
                  </div>
                  {variantSubtotal > 0 && (
                    <div className="flex justify-between items-center px-4 py-2 border-b border-amber-200 bg-amber-50 text-amber-800">
                      <span className="text-xs">Summe der Variantenleistungen</span>
                      <span className="text-xs font-medium">({formatAmount(variantSubtotal)} €)</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center px-4 py-2 border-b border-gray-200">
                    <span className="text-xs text-gray-600">MwSt. (20 %)</span>
                    <span className="text-xs font-medium">{formatAmount(tax)} €</span>
                  </div>
                  <div className="flex justify-between items-center px-4 py-2.5 bg-bauwerk-teal text-white">
                    <span className="text-sm font-bold">Gesamtbetrag</span>
                    <span className="text-sm font-bold">{formatAmount(total)} €</span>
                  </div>
                </div>
              </div>

              {perPhotoBreakdown.length > 0 && (
                <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                  <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Aufschlüsselung pro Bild
                  </p>
                  <div className="space-y-3">
                    {perPhotoBreakdown.map((entry, idx) => (
                      <div
                        key={`pp-${idx}`}
                        className="border border-bauwerk-mint/40 rounded-md overflow-hidden"
                      >
                        <div className="bg-bauwerk-mint/20 px-2.5 py-1.5 flex items-center justify-between gap-2">
                          <span className="text-xs font-semibold text-bauwerk-teal truncate">
                            {entry.photoLabel}
                            {entry.fileName && (
                              <span className="ml-1 font-normal text-gray-500">
                                · {entry.fileName}
                              </span>
                            )}
                          </span>
                          {entry.amount !== undefined && entry.amount > 0 && (
                            <span className="text-[11px] font-medium text-bauwerk-orange shrink-0 tabular-nums">
                              Aufmaß: {entry.amount.toFixed(2).replace('.', ',')} {entry.unit ?? 'm²'}
                            </span>
                          )}
                        </div>
                        <table className="w-full text-[11px]">
                          <thead>
                            <tr className="bg-gray-50 text-gray-500 uppercase tracking-wider text-[9px]">
                              <th className="text-left px-2 py-1 font-medium">Leistung</th>
                              <th className="text-right px-2 py-1 font-medium w-14">Menge</th>
                              <th className="text-left px-2 py-1 font-medium w-12">Einh.</th>
                              <th className="text-right px-2 py-1 font-medium w-16">EP (€)</th>
                              <th className="text-right px-2 py-1 font-medium w-16">Gesamt (€)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {entry.positions.map((pos, j) => (
                              <tr
                                key={`pp-${idx}-${j}`}
                                className={
                                  pos.variant
                                    ? 'bg-amber-50/60 italic'
                                    : (j % 2 === 1 ? 'bg-gray-50/60' : 'bg-white')
                                }
                              >
                                <td className="px-2 py-1 text-gray-800 max-w-[220px]" title={pos.beschreibung?.trim() || pos.name}>
                                  <div className="whitespace-pre-wrap">{pos.beschreibung?.trim() || pos.name}</div>
                                  <OfferPartSelect value={pos.teil} label={`Angebotsteil für ${entry.photoLabel}: ${pos.name}`}
                                    onChange={(teil) => { if (entry.photoId && pos.sourceIndex !== undefined) onPhotoTeilChange?.(entry.photoId, pos.sourceIndex, teil); }} />
                                  {pos.variant && (
                                    <span className="ml-1 text-[9px] font-semibold uppercase tracking-wider text-amber-700">
                                      Variante
                                    </span>
                                  )}
                                </td>
                                <td className="px-2 py-1 text-right text-gray-700 tabular-nums">
                                  {formatDecimal(pos.qty, 6, { minFractionDigits: 0 })}
                                </td>
                                <td className="px-2 py-1 text-gray-600">{pos.unit}</td>
                                <td className="px-2 py-1 text-right text-gray-700 tabular-nums">
                                  {formatAmount(pos.ep)}
                                </td>
                                <td
                                  className={
                                    pos.variant
                                      ? 'px-2 py-1 text-right font-medium text-amber-700 tabular-nums'
                                      : 'px-2 py-1 text-right font-semibold text-gray-900 tabular-nums'
                                  }
                                >
                                  {pos.variant ? `(${formatAmount(pos.total)})` : formatAmount(pos.total)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div className="flex justify-between items-center bg-emerald-50 border-t border-emerald-100 px-2.5 py-1.5">
                          <span className="text-[11px] font-medium text-emerald-800">
                            Zwischensumme
                          </span>
                          <span className="text-xs font-bold tabular-nums text-emerald-700">
                            {formatAmount(entry.subtotal)} €
                          </span>
                        </div>
                        {entry.positions.some((pos) => pos.variant) && (
                          <div className="flex justify-between items-center bg-amber-50 border-t border-amber-100 px-2.5 py-1.5 text-amber-800">
                            <span className="text-[11px] font-medium">Summe der Variantenleistungen</span>
                            <span className="text-xs font-bold tabular-nums">
                              ({formatAmount(entry.positions.reduce(
                                (sum, pos) => pos.variant ? sum + pos.total : sum,
                                0,
                              ))} €)
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                    {(() => {
                      const photoTotal = perPhotoBreakdown.reduce((s, e) => s + e.subtotal, 0);
                      const delta = photoTotal - subtotal;
                      const isConsistent = Math.abs(delta) < 0.005;
                      return (
                        <>
                          <div className="flex justify-between items-center bg-bauwerk-teal text-white px-3 py-1.5 rounded">
                            <span className="text-xs font-semibold">Summe Foto-Zwischensummen</span>
                            <span className="text-xs font-bold tabular-nums">
                              {formatAmount(photoTotal)} €
                            </span>
                          </div>
                          {!offerParts.A.included ? (
                            <p className="text-[10px] text-amber-800 mt-1">{OPTIONAL_PART_NOTE}</p>
                          ) : isConsistent ? (
                            <p className="text-[10px] text-emerald-700 dark:text-emerald-300 mt-1">
                              ✓ Foto-Mengen sind im Nettobetrag ({formatAmount(subtotal)} €) bereits enthalten.
                            </p>
                          ) : (
                            <p className="text-[10px] text-emerald-700 dark:text-emerald-300 mt-1">
                              Foto-Mengen sind im Nettobetrag ({formatAmount(subtotal)} €) bereits enthalten.
                              {' '}
                              Zusätzlich enthält der Nettobetrag {delta < 0 ? `${formatAmount(-delta)} €` : `abweichend ${formatAmount(delta)} €`} aus manuellen Positionen oder aktiven LV-Hauptpositionen.
                            </p>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}

              </div>
              {notes && (
                <div className="px-4 pb-3 border-t border-gray-100 pt-2">
                  <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Hinweise</p>
                  <p className="text-xs text-gray-600 leading-relaxed">{notes}</p>
                </div>
              )}

              {/* Footer bar */}
              <div className="bg-bauwerk-teal/5 border-t border-bauwerk-teal/15 px-4 py-2 flex items-center justify-between gap-3">
                <img
                  src={COMPANY.logoUrl2}
                  alt={COMPANY.brand}
                  className="h-5 w-auto object-contain opacity-70"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
                <div className="flex-1 text-center space-y-0.5">
                  {getCompanyContactLines().map((line, idx) => (
                    <p key={idx} className="text-[9px] text-gray-400">{line}</p>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Live-Vorschau + Layout-Regler */}
          <div className="space-y-3 rounded-lg border border-bauwerk-teal/20 bg-bauwerk-teal/5 p-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <h4 className="font-semibold text-bauwerk-teal flex items-center gap-2">
                <Eye className="w-4 h-4" />
                Live-Vorschau
                {previewLoading && (
                  <span className="text-xs font-normal text-muted-foreground">
                    (aktualisiere…)
                  </span>
                )}
              </h4>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowLayoutPanel(prev => !prev)}
                data-testid="button-toggle-layout-panel"
              >
                <Sliders className="w-4 h-4 mr-1.5" />
                Layout {showLayoutPanel ? 'ausblenden' : 'anpassen'}
              </Button>
            </div>

            {showLayoutPanel && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 rounded-md border border-bauwerk-teal/15 bg-white dark:bg-background p-3">
                {([
                  { key: 'headerContentGap', label: 'Abstand nach Header-Banner', min: 0, max: 30, step: 1 },
                  { key: 'titleTopGap', label: 'Abstand vor „ANGEBOT"-Titel', min: 0, max: 30, step: 1 },
                  { key: 'addressBottomGap', label: 'Abstand unter Adressblock', min: 0, max: 60, step: 1 },
                  { key: 'margin', label: 'Seitenrand (oben/seitlich)', min: 8, max: 25, step: 1 },
                  { key: 'descriptionTopSpacing', label: 'Abstand vor Leistungsbeschreibung', min: 0, max: 60, step: 1 },
                ] as const).map(cfg => (
                  <div key={cfg.key} className="space-y-1.5">
                    <div className="flex justify-between items-center">
                      <Label htmlFor={`layout-${cfg.key}`} className="text-xs">
                        {cfg.label}
                      </Label>
                      <span className="text-xs font-mono tabular-nums text-muted-foreground">
                        {pdfLayout[cfg.key]} mm
                      </span>
                    </div>
                    <Slider
                      id={`layout-${cfg.key}`}
                      value={[pdfLayout[cfg.key]]}
                      min={cfg.min}
                      max={cfg.max}
                      step={cfg.step}
                      onValueChange={(v) => updateLayout(cfg.key, v[0] ?? 0)}
                      data-testid={`slider-${cfg.key}`}
                    />
                  </div>
                ))}
                <div className="md:col-span-2 flex justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={resetLayout}
                    data-testid="button-reset-layout"
                  >
                    Auf Standard zurücksetzen
                  </Button>
                </div>
              </div>
            )}

            <div className="rounded-md border border-bauwerk-teal/15 bg-white overflow-hidden">
              {previewError ? (
                <div className="p-6 text-center text-sm text-destructive">
                  {previewError}
                </div>
              ) : projectDescriptionMissing ? (
                <div className="p-6 text-center text-sm text-muted-foreground space-y-2" data-testid="preview-missing-description-hint">
                  <p>Bitte Leistungsbeschreibung eintragen, um die Vorschau zu sehen.</p>
                  {onScrollToProjectDescription && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => onScrollToProjectDescription()}
                      data-testid="button-jump-to-description"
                    >
                      Zur Leistungsbeschreibung
                    </Button>
                  )}
                </div>
              ) : !customerData.name.trim() ? (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  Geben Sie einen Kundennamen ein, um die Vorschau zu erzeugen.
                </div>
              ) : !previewUrl ? (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  Vorschau wird erstellt…
                </div>
              ) : (
                <iframe
                  src={previewUrl}
                  title="PDF-Vorschau"
                  className="w-full h-[640px] bg-white"
                  data-testid="iframe-pdf-preview"
                />
              )}
            </div>
          </div>

          {/* PDF Export Einstellungen */}
          {Object.keys(lvMengenRefs).length > 0 && (
            <div className="flex items-center gap-3 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-lg px-4 py-2.5">
              <Checkbox
                id="showMeasurementSources"
                checked={showMeasurementSources}
                onCheckedChange={(checked) => toggleShowMeasurementSources(checked === true)}
              />
              <label
                htmlFor="showMeasurementSources"
                className="text-sm text-emerald-800 dark:text-emerald-200 cursor-pointer select-none flex items-center gap-1.5"
              >
                <Link2 className="w-3.5 h-3.5" />
                Aufmaß-Quellen im PDF anzeigen
              </label>
            </div>
          )}

          <QuoteTextPlausibility
            onImportMissing={onImportMissingServices}
            importSections={[{ id: "s7", title: projectSectionTitles.s7 || "Pauschalleistungen" }]}
            description={projectDescription ?? ""} positions={partPreviewRows
            .filter((row) => !row.variant)
            .map((row) => ({ id: row.id, kurztext: row.description, menge: row.quantity, einheit: row.unit,
              teil: row.teil === "B" || row.teil === "C" ? row.teil : "A" }))} />

          {/* Plausibilitätsprüfung — direkt vor den Aktionen, damit der Nutzer
              vor dem PDF-Export auf Inkonsistenzen aufmerksam wird. Nicht
              blockierend; bei vorhandenen Hinweisen erscheint zusätzlich eine
              Bestätigungsabfrage beim Klick auf „PDF erstellen". */}
          <PlausibilityBlock
            findings={plausibilityFindings}
            summary={plausibilitySummary}
            expanded={plausibilityExpanded}
            onToggle={() => setPlausibilityExpanded((v) => !v)}
            lvPositionen={lvPositionen}
            expandedPositionIds={plausibilityExpandedPositions}
            onExpandedPositionIdsChange={setPlausibilityExpandedPositions}
            onUpdateLvPositionEp={onUpdateLvPositionEp}
            onJumpToLv={(posId) => {
              if (!posId) return;
              const expandedIds = Array.from(plausibilityExpandedPositions);
              if (onJumpFromPlausibility) {
                onJumpFromPlausibility(posId, expandedIds);
              } else if (onScrollToLvPosition) {
                onScrollToLvPosition(posId);
              }
            }}
          />

          {projectId && (
            <div className="rounded-lg border border-bauwerk-teal/30 bg-bauwerk-mint/20 p-4 space-y-3">
              <div>
                <h4 className="font-semibold text-bauwerk-teal">Rechnung aus diesem Angebot</h4>
                <p className="text-sm text-gray-600">
                  Eine unabhängige Kopie der aktuellen Positionen und Fotos erstellen.
                  Das Angebot bleibt unverändert. Varianten werden erst bei Bedarf abgerechnet.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  disabled={creatingInvoice}
                  data-testid="button-create-invoice"
                  onClick={async () => {
                    if (creatingInvoice) return;
                    setCreatingInvoice(true);
                    try {
                      const quote = buildQuoteData();
                      if (!quote.items.length && !quote.perPhoto?.some((entry) => entry.positions.length)) {
                        throw new Error("Bitte zuerst mindestens eine Angebotsposition erfassen.");
                      }
                      const today = new Date();
                      const date = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
                      const relevantPhotos = photos.filter((photo) =>
                        selectedPhotoIds.has(photo.id) || perPhotoBreakdown.length > 0);
                      const photoIds = relevantPhotos.map((photo) => {
                        const id = photo.serverPhotoId ?? Number(photo.id);
                        if (!Number.isSafeInteger(id) || id <= 0) {
                          throw new Error("Bitte warten, bis alle Fotos vollständig hochgeladen sind.");
                        }
                        return id;
                      });
                      const invoice = await InvoiceApi.create(projectId, invoiceFromQuote(quote, date), photoIds);
                      navigate(`/invoice/${invoice.id}`);
                    } catch (error) {
                      toast({
                        title: "Rechnung konnte nicht erstellt werden",
                        description: error instanceof Error ? error.message : "Bitte erneut versuchen.",
                        variant: "destructive",
                      });
                    } finally {
                      setCreatingInvoice(false);
                    }
                  }}
                  className="bg-bauwerk-teal hover:bg-bauwerk-teal/90"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  {creatingInvoice ? "Rechnung wird kopiert…" : "Rechnung aus Angebot erstellen"}
                </Button>
                <Button type="button" variant="outline" onClick={() => navigate(`/project/${projectId}/invoices`)}>
                  Rechnungen öffnen
                </Button>
              </div>
            </div>
          )}

          {/* Aktionen */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              type="button"
              onClick={() => void generatePDF("download")}
              disabled={isGeneratingPDF}
              className="bg-bauwerk-teal hover:bg-bauwerk-teal/90 flex-1"
              data-testid="button-generate-pdf"
            >
              <Download className="w-4 h-4 mr-2" />
              {isGeneratingPDF ? 'Erstelle PDF...' : 'PDF erstellen'}
            </Button>
            <Button type="button" onClick={() => void generatePDF("preview")}
              disabled={isGeneratingPDF} variant="outline" className="flex-1"
              data-testid="button-pdf-dialog-preview">
              <Eye className="w-4 h-4 mr-2" />
              PDF-Vorschau
            </Button>

            <Button
              type="button"
              onClick={() => void sendQuoteEmail()}
              disabled={isSendingEmail || !customerData.email}
              variant="outline"
              className="border-bauwerk-orange text-bauwerk-orange hover:bg-bauwerk-orange hover:text-white flex-1"
            >
              <Mail className="w-4 h-4 mr-2" />
              {isSendingEmail ? 'Sende...' : 'Per E-Mail senden'}
            </Button>
          </div>

          {/* Service Status */}
          <div className="text-xs text-muted-foreground space-y-1">
            <p>📧 E-Mail Service: {emailService.isConfigured() ? 'Konfiguriert' : 'Benötigt Setup (fallback zu mailto)'}</p>
            <p>🤖 KI Vision: {import.meta.env.VITE_OPENAI_API_KEY ? 'Verfügbar' : 'API Key erforderlich'}</p>
          </div>

        </CardContent>
      </Card>

      {/* Hidden download link */}
      <a ref={downloadRef} style={{ display: 'none' }} />
      <Dialog open={pdfVariantDialogOpen} onOpenChange={setPdfVariantDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>PDF-Variante wählen</DialogTitle>
            <DialogDescription>Die Auswahl gilt nur für dieses PDF. Das Angebot im Editor bleibt unverändert.</DialogDescription>
          </DialogHeader>
          <fieldset className="space-y-2">
            <legend className="sr-only">PDF-Variante</legend>
            {QUOTE_PDF_VARIANTS.map((option) => <label key={option.value} className="flex cursor-pointer items-center gap-3 rounded-md border p-3 text-sm">
              <input type="radio" name="quote-pdf-variant" value={option.value} checked={pdfVariantDraft === option.value}
                onChange={() => setPdfVariantDraft(option.value)} />
              {option.label}
            </label>)}
          </fieldset>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setPdfVariantDialogOpen(false)}>Abbrechen</Button>
            <Button type="button" data-testid="button-confirm-pdf-variant" onClick={() => {
              setPdfVariantDialogOpen(false);
              setPdfExportVariant(pdfVariantDraft);
              pdfVariantRef.current = pdfVariantDraft;
              if (pendingPdfActionRef.current === "email") {
                void sendQuoteEmail(pdfVariantDraft);
              } else if (plausibilityFindings.length > 0) {
                setPlausibilityConfirmOpen(true);
              } else {
                void runGeneratePDF(pdfActionRef.current, pdfVariantDraft);
              }
            }}>PDF erstellen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={pdfDialog !== null} onOpenChange={(open) => { if (!open) closePdfDialog(); }}>
        <DialogContent className="flex h-[90dvh] w-[95vw] max-w-5xl flex-col gap-3 p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>PDF-Vorschau</DialogTitle>
            <DialogDescription>
              Das Angebot wird direkt in der App angezeigt. Ein neues Fenster ist nicht erforderlich.
            </DialogDescription>
          </DialogHeader>
          {pdfDialog && <iframe src={pdfDialog.url} title="Angebots-PDF im Dialog"
            className="min-h-0 w-full flex-1 rounded border bg-white" data-testid="iframe-pdf-dialog" />}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={closePdfDialog}>Schließen</Button>
            <Button type="button" onClick={() => { if (pdfDialog) downloadDialogPdf(pdfDialog); }}>
              <Download className="mr-2 h-4 w-4" />Herunterladen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lightbox modal for full-size photo */}
      <Dialog open={!!lightboxImage} onOpenChange={(open) => { if (!open) setLightboxImage(null); }}>
        <DialogContent className="max-w-3xl p-2 bg-black border-0 text-white [&>button]:bg-black/60 [&>button]:text-white">
          <DialogHeader className="sr-only">
            <DialogTitle>{lightboxImage?.alt ?? "Bild"} – Großansicht</DialogTitle>
            <DialogDescription>Vergrößerte Bildansicht. Mit Escape oder Schließen zurück zum Angebot.</DialogDescription>
          </DialogHeader>
          {lightboxImage && (
            <img
              src={lightboxImage.src}
              alt={lightboxImage.alt}
              className="w-full h-auto max-h-[85vh] object-contain rounded"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Bestätigungs-Abfrage vor PDF-Export bei vorhandenen Hinweisen */}
      <AlertDialog open={plausibilityConfirmOpen} onOpenChange={setPlausibilityConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              Plausibilitätshinweise vor PDF-Export
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  Es liegen <strong>{plausibilitySummary.total}</strong>{' '}
                  {plausibilitySummary.total === 1 ? 'Hinweis' : 'Hinweise'} zum Angebot vor:
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  {plausibilitySummary.byType['similar-different-ep'] > 0 && (
                    <li>
                      {plausibilitySummary.byType['similar-different-ep']}× ähnliche Positionen mit
                      abweichendem EP
                    </li>
                  )}
                  {plausibilitySummary.byType['ep-deviates-from-library'] > 0 && (
                    <li>
                      {plausibilitySummary.byType['ep-deviates-from-library']}× starke Abweichung vom
                      Bibliothekspreis
                    </li>
                  )}
                  {plausibilitySummary.byType['missing-value'] > 0 && (
                    <li>{plausibilitySummary.byType['missing-value']}× Position ohne Preis oder Menge</li>
                  )}
                  {plausibilitySummary.byType['same-position-different-unit'] > 0 && (
                    <li>
                      {plausibilitySummary.byType['same-position-different-unit']}× gleiche Position mit
                      unterschiedlichen Einheiten
                    </li>
                  )}
                </ul>
                <p>Möchten Sie das PDF trotzdem erstellen?</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-plausibility-cancel">Zurück & prüfen</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setPlausibilityConfirmOpen(false);
                void runGeneratePDF(pdfActionRef.current, pdfVariantRef.current);
              }}
              data-testid="button-plausibility-confirm"
            >
              Trotzdem PDF erstellen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// PlausibilityBlock: aufklappbarer Block, der die Befunde der Plausibilitäts-
// prüfung gruppiert nach betroffener LV-Position darstellt. Bei keinem
// Befund erscheint ein grüner „alles ok"-Hinweis. Pro betroffener LV-Position
// eine aufklappbare Karte mit den zugehörigen Befunden und einem direkten
// „Zur Position springen"-Knopf. Befunde, die mehrere Positionen gleichzeitig
// betreffen, erscheinen unter jeder ihrer Positionen. Befunde ohne LV-Bezug
// landen in einer Sammelgruppe „Allgemeine Hinweise" am Ende.
// ──────────────────────────────────────────────────────────────────────────
const FINDING_TYPE_LABEL: Record<PlausibilityFindingType, string> = {
  "similar-different-ep": "Ähnliche Positionen mit abweichendem EP",
  "ep-deviates-from-library": "EP weicht stark vom Bibliothekspreis ab",
  "missing-value": "Position ohne Preis oder Menge",
  "same-position-different-unit": "Gleiche Position, unterschiedliche Einheit",
};

/** Spezial-Key für die Sammelgruppe „Allgemeine Hinweise". */
const PLAUSI_ORPHAN_KEY = "__plausi_orphan__";

/** Höchste Schwere unter den Befunden bestimmt die Karten-Farbe. */
function findingsSeverity(findings: PlausibilityFinding[]): "error" | "warn" {
  return findings.some((f) => f.type === "missing-value") ? "error" : "warn";
}

function PlausibilityBlock({
  findings,
  summary,
  expanded,
  onToggle,
  onJumpToLv,
  lvPositionen,
  expandedPositionIds,
  onExpandedPositionIdsChange,
  onUpdateLvPositionEp,
}: {
  findings: PlausibilityFinding[];
  summary: { total: number; byType: Record<PlausibilityFindingType, number> };
  expanded: boolean;
  onToggle: () => void;
  onJumpToLv: (posId: string | undefined) => void;
  lvPositionen: LVPosition[];
  expandedPositionIds: Set<string>;
  onExpandedPositionIdsChange: (next: Set<string>) => void;
  onUpdateLvPositionEp?: (lvPositionId: string, ep: number, unit?: string) => void;
}) {
  // Reihenfolge & Anzeige-Daten der LV-Positionen für die Gruppierung.
  const grouped = useMemo(() => {
    const lvOrder = new Map<string, number>();
    lvPositionen.forEach((p, idx) => lvOrder.set(p.id, idx));
    const byPosId = new Map<string, PlausibilityFinding[]>();
    const orphans: PlausibilityFinding[] = [];
    findings.forEach((f) => {
      const lvIds = Array.from(
        new Set(
          f.positions
            .map((p) => p.lvPositionId)
            .filter((id): id is string => !!id && lvOrder.has(id)),
        ),
      );
      if (lvIds.length === 0) {
        orphans.push(f);
      } else {
        lvIds.forEach((id) => {
          const arr = byPosId.get(id) ?? [];
          arr.push(f);
          byPosId.set(id, arr);
        });
      }
    });
    const ordered = Array.from(byPosId.entries())
      .map(([posId, fs]) => {
        const pos = lvPositionen.find((p) => p.id === posId);
        return {
          posId,
          name: pos?.name ?? "(unbekannte Position)",
          positionsnummer: (lvOrder.get(posId) ?? 0) + 1,
          findings: fs,
        };
      })
      .sort((a, b) => a.positionsnummer - b.positionsnummer);
    return { ordered, orphans };
  }, [findings, lvPositionen]);

  const togglePositionCard = (key: string) => {
    const next = new Set(expandedPositionIds);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onExpandedPositionIdsChange(next);
  };

  if (findings.length === 0) {
    return (
      <div
        className="rounded-lg border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-800 p-3 flex items-center gap-2"
        data-testid="plausibility-empty"
      >
        <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
        <span className="text-sm font-medium text-emerald-800 dark:text-emerald-200">
          Keine Plausibilitätsprobleme gefunden
        </span>
      </div>
    );
  }

  return (
    <div
      className="rounded-lg border border-amber-300 bg-amber-50/70 dark:bg-amber-950/20 dark:border-amber-800 overflow-hidden"
      data-testid="plausibility-block"
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-amber-100/50 dark:hover:bg-amber-900/20 transition-colors"
        data-testid="plausibility-toggle"
      >
        <div className="flex items-center gap-2 min-w-0">
          <ShieldCheck className="w-5 h-5 text-amber-700 dark:text-amber-300 shrink-0" />
          <div className="min-w-0">
            <p className="font-semibold text-sm text-amber-900 dark:text-amber-100">
              Plausibilitätsprüfung: {summary.total} Hinweis
              {summary.total === 1 ? '' : 'e'}
              <span className="ml-2 text-[10px] font-normal uppercase tracking-wider text-amber-700/80 dark:text-amber-300/80">
                v2026-04-26 · gruppiert · EP-Edit
              </span>
            </p>
            <p className="text-xs text-amber-800/80 dark:text-amber-200/80 truncate">
              Bitte vor PDF-Export prüfen — die Erstellung ist trotzdem möglich.
            </p>
          </div>
        </div>
        {expanded ? (
          <ChevronDown className="w-4 h-4 text-amber-700 dark:text-amber-300 shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-amber-700 dark:text-amber-300 shrink-0" />
        )}
      </button>

      {expanded && (
        <div className="border-t border-amber-200 dark:border-amber-800 bg-white/70 dark:bg-background/40 p-3 space-y-2">
          {grouped.ordered.map((g) => (
            <PlausibilityPositionCard
              key={g.posId}
              cardKey={g.posId}
              positionsnummer={g.positionsnummer}
              positionName={g.name}
              findings={g.findings}
              expanded={expandedPositionIds.has(g.posId)}
              onToggle={() => togglePositionCard(g.posId)}
              onJumpToLv={() => onJumpToLv(g.posId)}
              onUpdateLvPositionEp={onUpdateLvPositionEp}
            />
          ))}
          {grouped.orphans.length > 0 && (
            <PlausibilityOrphanCard
              cardKey={PLAUSI_ORPHAN_KEY}
              findings={grouped.orphans}
              expanded={expandedPositionIds.has(PLAUSI_ORPHAN_KEY)}
              onToggle={() => togglePositionCard(PLAUSI_ORPHAN_KEY)}
              onUpdateLvPositionEp={onUpdateLvPositionEp}
            />
          )}
        </div>
      )}
    </div>
  );
}

function PlausibilityPositionCard({
  cardKey,
  positionsnummer,
  positionName,
  findings,
  expanded,
  onToggle,
  onJumpToLv,
  onUpdateLvPositionEp,
}: {
  cardKey: string;
  positionsnummer: number;
  positionName: string;
  findings: PlausibilityFinding[];
  expanded: boolean;
  onToggle: () => void;
  onJumpToLv: () => void;
  onUpdateLvPositionEp?: (lvPositionId: string, ep: number, unit?: string) => void;
}) {
  const severity = findingsSeverity(findings);
  const cardClasses =
    severity === "error"
      ? "border-red-300 bg-red-50 dark:bg-red-950/20 dark:border-red-800"
      : "border-amber-200 bg-white dark:bg-background dark:border-amber-800";
  const countClasses =
    severity === "error"
      ? "text-red-700 dark:text-red-300"
      : "text-amber-700 dark:text-amber-300";
  return (
    <div
      className={`rounded-md border ${cardClasses}`}
      data-plausibility-pos-card={cardKey}
      data-testid={`plausibility-position-card-${cardKey}`}
    >
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={onToggle}
          className="flex-1 flex items-center gap-2 text-left min-w-0"
          data-testid={`plausibility-position-toggle-${cardKey}`}
          aria-expanded={expanded}
        >
          {expanded ? (
            <ChevronDown className="w-4 h-4 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="w-4 h-4 shrink-0 text-muted-foreground" />
          )}
          <Badge variant="outline" className="text-[10px] shrink-0">
            Pos {positionsnummer}
          </Badge>
          <span className="text-sm font-medium text-foreground truncate flex-1" title={positionName}>
            {positionName}
          </span>
          <span className={`text-xs font-medium shrink-0 ${countClasses}`}>
            {findings.length} {findings.length === 1 ? "Hinweis" : "Hinweise"}
          </span>
        </button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 text-xs shrink-0"
          onClick={onJumpToLv}
          data-testid={`button-jump-lv-${cardKey}`}
        >
          <Link2 className="w-3.5 h-3.5 mr-1" />
          Zur Position springen
        </Button>
      </div>
      {expanded && (
        <div className="border-t border-amber-100 dark:border-amber-900/50 p-3 space-y-2">
          {findings.map((f) => (
            <PlausibilityFindingDetails
              key={f.id}
              finding={f}
              onUpdateLvPositionEp={onUpdateLvPositionEp}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PlausibilityOrphanCard({
  cardKey,
  findings,
  expanded,
  onToggle,
  onUpdateLvPositionEp,
}: {
  cardKey: string;
  findings: PlausibilityFinding[];
  expanded: boolean;
  onToggle: () => void;
  onUpdateLvPositionEp?: (lvPositionId: string, ep: number, unit?: string) => void;
}) {
  const severity = findingsSeverity(findings);
  const countClasses =
    severity === "error"
      ? "text-red-700 dark:text-red-300"
      : "text-amber-700 dark:text-amber-300";
  return (
    <div
      className="rounded-md border border-amber-200 bg-white dark:bg-background dark:border-amber-800"
      data-plausibility-pos-card={cardKey}
      data-testid="plausibility-orphan-card"
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-3 py-2 text-left"
        aria-expanded={expanded}
      >
        {expanded ? (
          <ChevronDown className="w-4 h-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="w-4 h-4 shrink-0 text-muted-foreground" />
        )}
        <span className="text-sm font-medium text-foreground flex-1">Allgemeine Hinweise</span>
        <span className={`text-xs font-medium shrink-0 ${countClasses}`}>
          {findings.length} {findings.length === 1 ? "Hinweis" : "Hinweise"}
        </span>
      </button>
      {expanded && (
        <div className="border-t border-amber-100 dark:border-amber-900/50 p-3 space-y-2">
          {findings.map((f) => (
            <PlausibilityFindingDetails
              key={f.id}
              finding={f}
              onUpdateLvPositionEp={onUpdateLvPositionEp}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PlausibilityFindingDetails({
  finding,
  onUpdateLvPositionEp,
}: {
  finding: PlausibilityFinding;
  onUpdateLvPositionEp?: (lvPositionId: string, ep: number, unit?: string) => void;
}) {
  const { toast } = useToast();
  const editablePositions = useMemo(
    () =>
      onUpdateLvPositionEp
        ? finding.positions.filter(
            (p) => typeof p.lvPositionId === "string" && p.lvPositionId.length > 0,
          )
        : [],
    [finding.positions, onUpdateLvPositionEp],
  );
  const suggestedEp =
    finding.type === "similar-different-ep" || finding.type === "ep-deviates-from-library"
      ? finding.suggestedEp
      : null;

  const [draftEps, setDraftEps] = useState<Record<string, string>>(() => {
    const d: Record<string, string> = {};
    for (const p of editablePositions) {
      d[p.key] = p.ep > 0 ? String(p.ep) : "";
    }
    return d;
  });

  // Bei jedem Re-Render des Findings (z.B. neue checkPositions nach
  // erfolgreichem EP-Update) die Drafts auf die aktuellen Werte zurücksetzen,
  // damit das Eingabefeld den frischen EP zeigt.
  useEffect(() => {
    setDraftEps((prev) => {
      const next: Record<string, string> = {};
      let changed = false;
      for (const p of editablePositions) {
        const current = p.ep > 0 ? String(p.ep) : "";
        // Vorhandene User-Eingabe behalten, sonst auf aktuellen EP setzen.
        if (prev[p.key] === undefined || prev[p.key] === "" || prev[p.key] === String(p.ep)) {
          next[p.key] = current;
          if (prev[p.key] !== current) changed = true;
        } else {
          next[p.key] = prev[p.key];
        }
      }
      // IDs entfernen, die nicht mehr betroffen sind.
      for (const k of Object.keys(prev)) {
        if (!(k in next)) changed = true;
      }
      return changed ? next : prev;
    });
  }, [editablePositions]);

  const submitEp = (key: string, lvPositionId: string, unit?: string) => {
    const raw = (draftEps[key] ?? "").trim().replace(",", ".");
    const value = parseFloat(raw);
    if (!Number.isFinite(value) || value < 0) {
      toast({
        variant: "destructive",
        description: "EP muss eine Zahl ≥ 0 sein.",
      });
      return;
    }
    onUpdateLvPositionEp?.(lvPositionId, Math.round(value * 100) / 100, unit);
    toast({ description: "EP gespeichert." });
  };

  return (
    <div
      className="rounded-md border border-amber-200/70 dark:border-amber-800/70 bg-amber-50/40 dark:bg-amber-950/10 p-3 space-y-2"
      data-testid={`plausibility-finding-${finding.type}`}
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-300 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground">
            {finding.title}
            <span className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground font-normal">
              {FINDING_TYPE_LABEL[finding.type]}
            </span>
          </p>
          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{finding.description}</p>
        </div>
      </div>

      {/* Detailtabelle der betroffenen Positionen */}
      {finding.positions.length > 0 && (
        <div className="rounded border border-amber-100 dark:border-amber-900/50 overflow-hidden">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="bg-amber-50 dark:bg-amber-950/30 text-amber-900 dark:text-amber-100 uppercase text-[9px] tracking-wider">
                <th className="text-left px-2 py-1 font-medium">Bezeichnung</th>
                <th className="text-left px-2 py-1 font-medium w-24">Quelle</th>
                <th className="text-right px-2 py-1 font-medium w-16">Menge</th>
                <th className="text-left px-2 py-1 font-medium w-12">Einh.</th>
                <th className="text-right px-2 py-1 font-medium w-20">EP (€)</th>
              </tr>
            </thead>
            <tbody>
              {finding.positions.map((p, idx) => (
                <tr
                  key={`${finding.id}-${p.key}`}
                  className={idx % 2 === 0 ? 'bg-white dark:bg-background' : 'bg-amber-50/30 dark:bg-amber-950/10'}
                >
                  <td className="px-2 py-1 truncate max-w-[180px]" title={p.name}>
                    {p.name}
                  </td>
                  <td className="px-2 py-1 text-muted-foreground">
                    {p.source === 'lv' && 'LV'}
                    {p.source === 'manual' && 'Manuell'}
                    {p.source === 'photo-agg' && 'Foto-Menge'}
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums">
                    {formatDecimal(p.qty, 6, { minFractionDigits: 0 })}
                  </td>
                  <td className="px-2 py-1">{p.unit}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{formatAmount(p.ep)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* EP-Stat-Zusammenfassung bei "ähnliche Positionen" */}
      {finding.type === 'similar-different-ep' && (
        <div className="flex items-center flex-wrap gap-1.5 text-[10px]">
          <span className="text-muted-foreground">Vorkommen:</span>
          {finding.epStats.map((s) => (
            <span
              key={s.ep}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-900 dark:text-amber-100 font-medium tabular-nums"
            >
              {formatAmount(s.ep)} € × {s.count}
            </span>
          ))}
          <span className="ml-1 text-emerald-700 dark:text-emerald-300 font-medium">
            Vorschlag: {formatAmount(finding.suggestedEp)} €/{finding.unit}
          </span>
        </div>
      )}

      {/* Inline-EP-Editor für betroffene LV-Positionen */}
      {editablePositions.length > 0 && (
        <div className="space-y-1.5 pt-1 border-t border-amber-100 dark:border-amber-900/40">
          {editablePositions.map((p) => {
            const lvId = p.lvPositionId as string;
            const draft = draftEps[p.key] ?? "";
            const draftNum = parseFloat(draft.replace(",", "."));
            const isDirty =
              Number.isFinite(draftNum) &&
              Math.round(draftNum * 100) / 100 !== Math.round(p.ep * 100) / 100;
            return (
              <div
                key={`edit-${finding.id}-${p.key}`}
                className="flex items-center flex-wrap gap-1.5 text-[11px]"
                data-testid={`plausibility-ep-edit-${finding.id}-${p.key}`}
              >
                <span className="text-muted-foreground">EP ändern für</span>
                <span className="font-medium text-foreground truncate max-w-[160px]" title={p.name}>
                  „{p.name}"
                </span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={draft}
                  onChange={(e) =>
                    setDraftEps((prev) => ({ ...prev, [p.key]: e.target.value }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      submitEp(p.key, lvId, p.unit);
                    }
                  }}
                  className="w-20 text-right tabular-nums py-0.5 px-1.5 rounded border border-input bg-background focus:outline-none focus:ring-1 focus:ring-sky-500"
                  data-testid={`plausibility-ep-input-${finding.id}-${p.key}`}
                />
                <span className="text-muted-foreground">€/{p.unit}</span>
                {suggestedEp !== null && Math.round(suggestedEp * 100) / 100 !== Math.round(p.ep * 100) / 100 && (
                  <button
                    type="button"
                    onClick={() =>
                      setDraftEps((prev) => ({ ...prev, [p.key]: String(suggestedEp) }))
                    }
                    className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200 hover:bg-emerald-200 dark:hover:bg-emerald-900/60 font-medium tabular-nums"
                    title={`Vorschlag ${formatAmount(suggestedEp)} € übernehmen`}
                    data-testid={`plausibility-ep-suggest-${finding.id}-${p.key}`}
                  >
                    {formatAmount(suggestedEp)} € übernehmen
                  </button>
                )}
                {finding.type === "ep-deviates-from-library" &&
                  Math.round(finding.libraryEp * 100) / 100 !== Math.round(p.ep * 100) / 100 && (
                    <span className="text-[10px] text-muted-foreground tabular-nums">
                      Bibliothek: {formatAmount(finding.libraryEp)} €
                    </span>
                  )}
                <button
                  type="button"
                  disabled={!isDirty}
                  onClick={() => submitEp(p.key, lvId, p.unit)}
                  className="ml-auto text-[10px] px-2 py-0.5 rounded bg-sky-600 text-white hover:bg-sky-700 font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid={`plausibility-ep-save-${finding.id}-${p.key}`}
                >
                  Speichern
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}